import React, { useState, useEffect, useMemo, useRef } from 'react';
import { doc, getDoc, updateDoc, addDoc, serverTimestamp, orderBy, deleteDoc, runTransaction, limit, setDoc, query, collection, where, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';


import { useToast } from '../../contexts/ToastContext';
import { geocodeAddress } from '../../utils/helpers';
import MembershipBadge from '../../components/shared/MembershipBadge';
import AnalyticsCard from '../../components/ui/AnalyticsCard';
import RevenueChart from '../../components/ui/RevenueChart';
import BookingDetailsCard from '../../features/bookings/BookingDetailsCard';
import InvoiceModal from '../../components/ui/Modal';
import ContractModal from '../../components/ui/Modal';
import ReturnConfirmationModal from '../../components/ui/Modal';
import DeliveryConfirmationModal from '../../components/ui/Modal';
import DeleteConfirmationModal from '../../components/ui/Modal';
import ManualOrderModal from '../../components/ui/Modal';
import AddListingForm from './AddListingForm';
import StorefrontEditor from './StorefrontEditor';
import VendorCardGeneratorModal from './VendorCardGeneratorModal';
import VendorCalendar from './VendorCalendar';
import VendorFinanceTab from './VendorFinanceTab';

const PlannerIcons = {
    Grid: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>,
    CalendarIcon: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
    Dollar: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    Users: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>,
    Box: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>,
    Star: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>,
    Chat: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>,
    Trash: () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
};

export default function VendorDashboard({ user, db, storage, navigateTo, initialData, isMapsScriptLoaded, functions, isUserAuthenticated }) {
    const [listings, setListings] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [reviews, setReviews] = useState([]); 
    const [loading, setLoading] = useState(true);
    
    // Modal states
    const [bookingToInvoice, setBookingToInvoice] = useState(null);
    const [bookingToContract, setBookingToContract] = useState(null);
    const [editingOrder, setEditingOrder] = useState(null);
    const [showCardGenerator, setShowCardGenerator] = useState(false); 
    const [showStorefrontEditor, setShowStorefrontEditor] = useState(false); 
    const [bookingToAccept, setBookingToAccept] = useState(null);
    const [bookingToDecline, setBookingToDecline] = useState(null);
    const [showReturnModal, setShowReturnModal] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [showManualOrderForm, setShowManualOrderForm] = useState(false);
    const [showLogisticsPlanner, setShowLogisticsPlanner] = useState(false);
    const [logisticsDate, setLogisticsDate] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingListingId, setEditingListingId] = useState(null);
    const [listingToDelete, setListingToDelete] = useState(null);
    
    // Navigation & Filter States
    const [activeTab, setActiveTab] = useState('overview');
    const [inventorySubTab, setInventorySubTab] = useState('items');
    const [marketingSubTab, setMarketingSubTab] = useState('reviews');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('newest');
    const [orderStatusFilter, setOrderStatusFilter] = useState('all');
    const [expandedBookingId, setExpandedBookingId] = useState(null);
    const [bookingErrors, setBookingErrors] = useState({});
    const [listingCategoryFilter, setListingCategoryFilter] = useState('all');
    const [visibleBookingCount, setVisibleBookingCount] = useState(20);

    const toast = useToast();

    useEffect(() => {
        setVisibleBookingCount(20);
    }, [orderStatusFilter, searchTerm, sortBy]);

    // --- DATA FETCHING ---
    useEffect(() => {
        if (!user || !user.uid) return;
        const qListings = query(collection(db, "listings"), where("vendorId", "==", user.uid));
        const unsubListings = onSnapshot(qListings, (snapshot) => {
            setListings(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const qBookings = query(collection(db, "bookings"), where("vendorId", "==", user.uid));
        const unsubBookings = onSnapshot(qBookings, (snapshot) => {
            setBookings(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const qReviews = query(collection(db, "reviews"), where("vendorId", "==", user.uid));
        const unsubReviews = onSnapshot(qReviews, (snapshot) => {
            setReviews(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });

        return () => { unsubListings(); unsubBookings(); unsubReviews(); };
    }, [user, db]);

    // --- COMPUTED PROPERTIES ---
    const uniqueCategories = useMemo(() => {
        const cats = ['all'];
        listings.forEach(l => { if (l.category && !cats.includes(l.category)) cats.push(l.category); });
        return cats;
    }, [listings]);

    const filteredListings = useMemo(() => {
        if (listingCategoryFilter === 'all') return listings;
        return listings.filter(l => l.category === listingCategoryFilter);
    }, [listings, listingCategoryFilter]);

    const filteredAndSortedBookings = useMemo(() => {
        let result = [...bookings];
        
        if (orderStatusFilter !== 'all') {
            if (orderStatusFilter === 'pending') result = result.filter(b => b.status === 'pending');
            else if (orderStatusFilter === 'active') result = result.filter(b => ['awaitingPayment', 'deposit-paid', 'booked', 'awaitingFinalPayment', 'awaitingPlannerConfirmation', 'delivered', 'awaitingPickup'].includes(b.status));
            else if (orderStatusFilter === 'completed') result = result.filter(b => ['paid', 'returned', 'completed'].includes(b.status));
            else if (orderStatusFilter === 'cancelled') result = result.filter(b => ['cancelled', 'declined'].includes(b.status));
        }

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(b => 
                (b.renterName || '').toLowerCase().includes(term) ||
                (b.bookingNumber || '').toLowerCase().includes(term) ||
                (b.listingTitle || '').toLowerCase().includes(term)
            );
        }

        result.sort((a, b) => {
            if (sortBy === 'newest') return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
            if (sortBy === 'date-asc') return (a.date || '').localeCompare(b.date || '');
            if (sortBy === 'total-desc') return (b.totalCost || 0) - (a.totalCost || 0);
            return 0;
        });

        return result;
    }, [bookings, orderStatusFilter, searchTerm, sortBy]);

    const analytics = useMemo(() => {
        const now = new Date();
        const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        let monthlyRev = 0;
        let totalRev = 0;
        let pending = 0;
        const itemCounts = {};

        bookings.forEach(b => {
            if (['paid', 'completed', 'delivered', 'returned'].includes(b.status)) {
                totalRev += (b.totalCost || 0);
                const bDate = b.date ? b.date.substring(0, 7) : null;
                if (bDate === currentMonthStr) monthlyRev += (b.totalCost || 0);
            }
            if (b.status === 'pending') pending++;
            if (!['cancelled', 'declined'].includes(b.status)) {
                itemCounts[b.listingTitle] = (itemCounts[b.listingTitle] || 0) + b.quantity;
            }
        });

        let topListing = 'N/A';
        let maxCount = 0;
        Object.entries(itemCounts).forEach(([title, count]) => {
            if (count > maxCount) { maxCount = count; topListing = title; }
        });

        return {
            monthlyRevenue: monthlyRev.toFixed(2),
            totalRevenue: totalRev.toFixed(2),
            totalBookings: bookings.length,
            pendingRequests: pending,
            topListing
        };
    }, [bookings]);

    const needsActionBookings = useMemo(() => {
        return bookings.filter(b => b.status === 'pending').sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    }, [bookings]);

    // --- HELPER FUNCTIONS ---
    const getStatusClasses = (status) => {
        switch (status) {
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'awaitingPayment':
            case 'awaitingFinalPayment':
            case 'awaitingPlannerConfirmation': return 'bg-orange-100 text-orange-800';
            case 'deposit-paid': return 'bg-teal-100 text-teal-800';
            case 'paid': return 'bg-green-100 text-green-800';
            case 'booked': return 'bg-green-100 text-green-800'; 
            case 'delivered': return 'bg-blue-100 text-blue-800';
            case 'awaitingPickup': return 'bg-purple-100 text-purple-800';
            case 'returned': return 'bg-green-100 text-green-800';
            case 'cancelled':
            case 'declined': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const handleViewBooking = (bookingId) => {
        setExpandedBookingId(bookingId);
        setActiveTab('orders');
        setTimeout(() => {
            const el = document.getElementById(bookingId);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
    };

    const handleStartChatFromBooking = async (booking) => {
        if (!user || !booking.renterId || booking.isGuestBooking) {
            toast.error("Cannot start chat. Renter is a guest or missing information.");
            return;
        }

        const renterEmail = booking.renterEmail;
        const renterName = booking.renterName || booking.renterEmail;

        const chatId = [user.uid, booking.renterId].sort().join('_');
        const chatDocRef = doc(db, "chats", chatId);

        await setDoc(chatDocRef, {
            participants: [user.uid, booking.renterId],
            participantEmails: [user.email, renterEmail],
            participantInfo: {
                [user.uid]: { name: user.vendorName || user.email, email: user.email, profilePicUrl: user.profilePicUrl || null },
                [booking.renterId]: { name: renterName, email: renterEmail, profilePicUrl: null } 
            },
            updatedAt: serverTimestamp(),
        }, { merge: true });

        const prefillMessage = `Hi ${renterName}, regarding your booking for "${booking.listingTitle}" (Order #${booking.bookingNumber || booking.id.slice(0,6)}): `;
        navigateTo('messages', { chatId, prefillMessage });
    };

    // --- ACTIONS & MODALS ---
    const handleSaveManualOrder = async (orderData) => {
        try {
            await runTransaction(db, async (transaction) => {
                const newBookingRef = doc(collection(db, "bookings"));
                const listingDocs = await Promise.all(
                    orderData.manualOrderItems.map(item => transaction.get(doc(db, "listings", item.listingId)))
                );
                for (let i = 0; i < orderData.manualOrderItems.length; i++) {
                    const item = orderData.manualOrderItems[i];
                    const listingDoc = listingDocs[i];
                    if (!listingDoc.exists()) throw new Error(`Listing "${item.description}" no longer exists.`);
                    
                    const listingData = listingDoc.data();
                    const newBookedDates = { ...listingData.bookedDates };
                    const bufferDays = parseInt(listingData.bufferDays) || 0;
                    
                    let currentDate = new Date(orderData.date);
                    let stopDate = new Date(orderData.returnDate);
                    stopDate.setDate(stopDate.getDate() + bufferDays);

                    while(currentDate <= stopDate) {
                        const dateString = currentDate.toISOString().split('T')[0];
                        const alreadyBooked = newBookedDates[dateString] || 0;
                        if (alreadyBooked + item.quantity > listingData.inventory) {
                            throw new Error(`Not enough inventory for "${item.description}" on ${dateString}.`);
                        }
                        newBookedDates[dateString] = alreadyBooked + item.quantity;
                        currentDate.setDate(currentDate.getDate() + 1);
                    }
                    transaction.update(listingDoc.ref, { bookedDates: newBookedDates });
                }
                transaction.set(newBookingRef, orderData);
            });
            toast.success("Manual order saved!");
        } catch (err) {
            throw err; // Passed up to modal
        }
    };

    const handleMarkAsDelivered = async (bookingId, renterId, listingTitle) => {
        const bookingRef = doc(db, "bookings", bookingId);
        await updateDoc(bookingRef, { status: 'awaitingPlannerConfirmation' });
        // Assume createNotification is passed down or imported globally
        toast.success("Marked as delivered.");
    };

    const handleDeleteListing = async () => {
        if (!listingToDelete) return;
        try {
            await deleteDoc(doc(db, "listings", listingToDelete.id));
            toast.success("Listing deleted.");
        } catch (error) {
            console.error("Error deleting listing:", error);
            toast.error("Failed to delete the listing.");
        } finally {
            setListingToDelete(null); 
        }
    };

    const handleToggleFeatured = async (listing) => {
        try {
            const listingRef = doc(db, "listings", listing.id);
            await updateDoc(listingRef, { isFeatured: !listing.isFeatured });
            toast.success(listing.isFeatured ? "Removed from featured items." : "Item featured successfully!");
        } catch (error) {
            console.error("Error toggling featured status:", error);
            toast.error("Failed to update featured status.");
        }
    };

    const handleConfirmAcceptBooking = async (booking, paymentTerms) => {
        try {
            await runTransaction(db, async (transaction) => {
                const listingRef = doc(db, "listings", booking.listingId);
                const bookingRef = doc(db, "bookings", booking.id);
                const listingDoc = await transaction.get(listingRef);

                if (!listingDoc.exists()) throw new Error("Listing no longer exists.");

                const listingData = listingDoc.data();
                const newBookedDates = { ...listingData.bookedDates };
                const bufferDays = parseInt(listingData.bufferDays) || 0;
                
                let currentDate = new Date(booking.date);
                let stopDate = new Date(booking.returnDate);
                stopDate.setDate(stopDate.getDate() + bufferDays);

                const optionName = booking.selectedOption || 'default';
                const inventorySource = listingData.options?.find(opt => opt.name === optionName) || listingData;

                while(currentDate <= stopDate) {
                    const dateString = currentDate.toISOString().split('T')[0];
                    if (!newBookedDates[dateString]) newBookedDates[dateString] = {};
                    
                    const currentBookedCount = newBookedDates[dateString][optionName] || 0;
                    if (currentBookedCount + booking.quantity > inventorySource.inventory) {
                        throw new Error(`Not enough inventory for "${listingData.title} - ${optionName}" on ${dateString}.`);
                    }
                    newBookedDates[dateString][optionName] = currentBookedCount + booking.quantity;
                    currentDate.setDate(currentDate.getDate() + 1);
                }

                const isNoPaymentRequired = paymentTerms.type === 'no_payment';

                transaction.update(listingRef, { bookedDates: newBookedDates });
                transaction.update(bookingRef, {
                    status: isNoPaymentRequired ? 'deposit-paid' : 'awaitingPayment',
                    paymentTerms: isNoPaymentRequired ? null : {
                        ...paymentTerms,
                        deadline: paymentTerms.deadline
                    }
                });
            });

            // Send beautifully styled acceptance email
            try {
                const isNoPaymentRequired = paymentTerms.type === 'no_payment';
                const subject = isNoPaymentRequired 
                    ? `Booking Confirmed for ${booking.listingTitle}`
                    : `Action Required: Your booking for ${booking.listingTitle} is accepted!`;
                
                const deadlineText = !isNoPaymentRequired 
                    ? `<p style="font-size: 16px; margin-bottom: 20px; line-height: 1.5;">To confirm your reservation, please complete the payment of <strong>$${paymentTerms.amount.toFixed(2)}</strong> by <strong>${paymentTerms.deadline.toLocaleDateString()}</strong>.</p>
                       <div style="text-align: center; margin: 30px 0;">
                           <a href="${window.location.origin}/#bookingDetail/${booking.id}" style="background: #10b981; color: #ffffff; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: bold; font-size: 16px; display: inline-block;">Pay Now</a>
                       </div>`
                    : `<p style="font-size: 16px; margin-bottom: 20px; line-height: 1.5;">No payment is required at this time.</p>
                       <div style="text-align: center; margin: 30px 0;">
                           <a href="${window.location.origin}/#bookingDetail/${booking.id}" style="background: #3b82f6; color: #ffffff; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: bold; font-size: 16px; display: inline-block;">View Booking</a>
                       </div>`;

                const html = `
                    <div style="font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; max-w: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.08); border: 1px solid #f3f4f6;">
                        <div style="background: linear-gradient(135deg, #10b981 0%, #059669 100%); padding: 30px 20px; text-align: center;">
                            <h1 style="color: #ffffff; margin: 0; font-size: 24px; font-weight: bold;">Booking Accepted! 🎉</h1>
                        </div>
                        <div style="padding: 30px; color: #374151;">
                            <p style="font-size: 16px; margin-bottom: 20px;">Hi ${booking.renterName},</p>
                            <p style="font-size: 16px; margin-bottom: 20px; line-height: 1.5;">Great news! <strong>${user.vendorName || 'The vendor'}</strong> has accepted your booking request for <strong>${booking.listingTitle}</strong>.</p>
                            <div style="background-color: #f9fafb; border-left: 4px solid #10b981; padding: 15px; margin-bottom: 25px; border-radius: 4px;">
                                <p style="margin: 0 0 10px 0;"><strong>Order #:</strong> ${booking.bookingNumber || booking.id.slice(0,6)}</p>
                                <p style="margin: 0 0 10px 0;"><strong>Dates:</strong> ${booking.date} to ${booking.returnDate}</p>
                                <p style="margin: 0;"><strong>Total Cost:</strong> $${(booking.totalCost || 0).toFixed(2)}</p>
                            </div>
                            ${deadlineText}
                            <p style="font-size: 14px; color: #6b7280; margin-top: 30px; text-align: center;">Thanks for using Host-it!</p>
                        </div>
                    </div>
                `;

                const sendBrevoNotification = httpsCallable(functions, 'sendBrevoNotification');
                const textContent = html.replace(/<[^>]*>?/gm, '\n').replace(/\n+/g, '\n').trim();
                
                await sendBrevoNotification({
                    toEmail: booking.renterEmail,
                    subject: subject,
                    textContent: textContent,
                    htmlContent: html
                });
            } catch (e) {
                console.error("Failed to send acceptance email:", e);
            }

            toast.success("Booking accepted!");
            setBookingToAccept(null);
        } catch (error) {
            console.error("Error accepting booking:", error);
            toast.error(`Failed to accept: ${error.message}`);
            setBookingToAccept(null);
        }
    };

    const executeDeclineBooking = async (booking) => {
        try {
            await runTransaction(db, async (transaction) => {
                const listingRef = doc(db, "listings", booking.listingId);
                const bookingRef = doc(db, "bookings", booking.id);
                const listingDoc = await transaction.get(listingRef);
                if (!listingDoc.exists()) throw new Error("Listing no longer exists.");
                
                const listingData = listingDoc.data();
                const newBookedDates = { ...listingData.bookedDates };
                const bufferDays = parseInt(listingData.bufferDays) || 0;
                
                let currentDate = new Date(booking.date);
                let stopDate = new Date(booking.returnDate);
                stopDate.setDate(stopDate.getDate() + bufferDays);

                while(currentDate <= stopDate) {
                    const dateString = currentDate.toISOString().split('T')[0];
                    if (newBookedDates[dateString]) {
                        newBookedDates[dateString] -= booking.quantity;
                        if (newBookedDates[dateString] <= 0) delete newBookedDates[dateString];
                    }
                    currentDate.setDate(currentDate.getDate() + 1);
                }
                transaction.update(listingRef, { bookedDates: newBookedDates });
                transaction.update(bookingRef, { status: 'declined' });
            });

            // Send beautifully styled decline email
            try {
                const html = `
                    <div style="font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; max-w: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.08); border: 1px solid #f3f4f6;">
                        <div style="background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%); padding: 30px 20px; text-align: center;">
                            <h1 style="color: #ffffff; margin: 0; font-size: 24px; font-weight: bold;">Update on Your Booking</h1>
                        </div>
                        <div style="padding: 30px; color: #374151;">
                            <p style="font-size: 16px; margin-bottom: 20px;">Hi ${booking.renterName},</p>
                            <p style="font-size: 16px; margin-bottom: 20px; line-height: 1.5;">Unfortunately, <strong>${user.vendorName || 'the vendor'}</strong> is unable to accommodate your booking request for <strong>${booking.listingTitle}</strong> at this time.</p>
                            <div style="background-color: #f9fafb; border-left: 4px solid #ef4444; padding: 15px; margin-bottom: 25px; border-radius: 4px;">
                                <p style="margin: 0 0 10px 0;"><strong>Order #:</strong> ${booking.bookingNumber || booking.id.slice(0,6)}</p>
                                <p style="margin: 0;"><strong>Dates Requested:</strong> ${booking.date} to ${booking.returnDate}</p>
                            </div>
                            <p style="font-size: 16px; margin-bottom: 20px; line-height: 1.5;">Any temporary holds on your card have been released. We encourage you to browse other amazing items on our marketplace!</p>
                            <div style="text-align: center; margin: 30px 0;">
                                <a href="${window.location.origin}/#listings" style="background: #ec4899; color: #ffffff; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: bold; font-size: 16px; display: inline-block;">Find Another Item</a>
                            </div>
                            <p style="font-size: 14px; color: #6b7280; margin-top: 30px; text-align: center;">Thanks for using Host-it!</p>
                        </div>
                    </div>
                `;

                const sendBrevoNotification = httpsCallable(functions, 'sendBrevoNotification');
                const textContent = html.replace(/<[^>]*>?/gm, '\n').replace(/\n+/g, '\n').trim();
                
                await sendBrevoNotification({
                    toEmail: booking.renterEmail,
                    subject: `Update on your booking request for ${booking.listingTitle}`,
                    textContent: textContent,
                    htmlContent: html
                });
            } catch (e) {
                console.error("Failed to send decline email:", e);
            }

            toast.success("Booking declined.");
            setBookingToDecline(null);
        } catch (error) {
            console.error("Error declining booking:", error);
            toast.error("Failed to decline booking.");
            setBookingToDecline(null);
        }
    };

    const handleSendContract = async (content) => {
        if (!bookingToContract) return;
        const bookingRef = doc(db, "bookings", bookingToContract.id);
        await updateDoc(bookingRef, {
            contractStatus: 'sent',
            contractHtml: content,
            contractSentAt: serverTimestamp()
        });
        toast.success("Contract sent to renter!");
        setBookingToContract(null);
    };

    const handleRequestFinalPayment = async (booking) => {
        const bookingRef = doc(db, "bookings", booking.id);
        const totalPaid = (booking.payments || []).reduce((acc, p) => acc + p.amount, 0);
        const finalAmount = booking.totalCost - totalPaid;

        if (finalAmount <= 0) {
            await updateDoc(bookingRef, { status: 'paid' });
            return;
        }

        const deadline = new Date();
        deadline.setDate(deadline.getDate() + 2);

        await updateDoc(bookingRef, {
            status: 'awaitingFinalPayment',
            paymentTerms: {
                ...booking.paymentTerms,
                finalAmount: finalAmount,
                finalDeadline: deadline,
            },
        });
        toast.success("Final payment requested.");
    };

    // --- RENDER ---
    if (loading) {
        return (
            <div className="container mx-auto space-y-8 p-4">
                <div className="h-8 w-48 bg-gray-200 rounded animate-pulse mb-8"></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                    {[1, 2, 3, 4, 5].map(i => (
                        <div key={i} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center h-24 animate-pulse">
                            <div className="bg-gray-200 rounded-full h-14 w-14 flex-shrink-0"></div>
                            <div className="ml-5 space-y-2 flex-grow">
                                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                                <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
    
    return (
        <div className="container mx-auto space-y-6 pb-12">
            {/* Global Modals */}
            {bookingToInvoice && <InvoiceModal booking={bookingToInvoice} user={user} onClose={() => setBookingToInvoice(null)} db={db} />}
            {bookingToContract && <ContractModal booking={bookingToContract} user={user} onClose={() => setBookingToContract(null)} onSend={handleSendContract} />}
            {showCardGenerator && <VendorCardGeneratorModal user={user} onClose={() => setShowCardGenerator(false)} />}
            {showStorefrontEditor && <StorefrontEditor user={user} listings={listings} reviews={reviews} onClose={() => setShowStorefrontEditor(false)} db={db} storage={storage} />}
            {bookingToAccept && <AcceptBookingModal booking={bookingToAccept} onClose={() => setBookingToAccept(null)} onConfirm={handleConfirmAcceptBooking} />}
            {bookingToDecline && <DeclineConfirmationModal booking={bookingToDecline} onClose={() => setBookingToDecline(null)} onConfirm={executeDeclineBooking} />}
            {showManualOrderForm && <ManualOrderModal user={user} db={db} listings={listings} onClose={() => setShowManualOrderForm(false)} onSave={handleSaveManualOrder} />}
            {showLogisticsPlanner && <LogisticsPlanner user={user} db={db} functions={functions} initialDate={logisticsDate} onClose={() => setShowLogisticsPlanner(false)} />}
            {listingToDelete && <DeleteConfirmationModal onConfirm={handleDeleteListing} onCancel={() => setListingToDelete(null)} />}
            {showReturnModal && <ReturnConfirmationModal booking={selectedBooking} onClose={() => setShowReturnModal(false)} />}
            {editingOrder && <VendorOrderEditModal booking={editingOrder} onClose={() => setEditingOrder(null)} db={db} />}
            
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div>
                    <div className="flex items-center gap-3">
                        <h2 className="text-3xl font-bold text-gray-900">Dashboard</h2>
                        <MembershipBadge user={user} className="text-xs px-2 py-0.5 hidden sm:inline-flex" />
                    </div>
                    <p className="text-gray-500 text-sm mt-1">Welcome back, {user.vendorName || user.firstName || 'Partner'}</p>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-3 w-full md:w-auto">
                    <button onClick={() => setShowStorefrontEditor(true)} className="flex items-center justify-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-lg hover:shadow-lg transition font-medium text-sm shadow-md flex-1 sm:flex-none">
                        <span>✨</span> Edit Storefront
                    </button>
                    <button onClick={() => setShowManualOrderForm(true)} className="flex items-center justify-center gap-2 bg-green-50 text-green-700 border border-green-200 px-4 py-2 rounded-lg hover:bg-green-100 transition font-medium text-sm flex-1 sm:flex-none">
                        <span>📝</span> Manual Order
                    </button>
                    <button onClick={() => { setEditingListingId(null); setShowAddForm(true); setActiveTab('listings'); setInventorySubTab('items'); }} className="flex items-center justify-center gap-2 bg-pink-600 text-white px-4 py-2 rounded-lg hover:bg-pink-700 transition font-medium text-sm shadow-md flex-1 sm:flex-none">
                        <span>+</span> Add Listing
                    </button>
                </div>
            </div>
            
            {/* Main Navigation Tabs */}
            <div className="overflow-x-auto custom-scrollbar no-scrollbar-mobile pb-4 mb-4 mt-2">
                <div className="flex gap-6 items-end min-w-max">
                    {[
                        {
                            group: 'Business Hub',
                            tabs: [
                                { id: 'overview', label: 'Overview', icon: PlannerIcons.Grid },
                                { id: 'orders', label: 'Orders & Calendar', icon: PlannerIcons.CalendarIcon }
                            ]
                        },
                        {
                            group: 'Management',
                            tabs: [
                                { id: 'listings', label: 'Inventory', icon: PlannerIcons.Box },
                                { id: 'crm', label: 'CRM & Leads', icon: PlannerIcons.Users },
                            ]
                        },
                        {
                            group: 'Growth & Revenue',
                            tabs: [
                                { id: 'marketing', label: 'Marketing', icon: PlannerIcons.Star },
                                { id: 'finance', label: 'Finance', icon: PlannerIcons.Dollar }
                            ]
                        }
                    ].map((category, idx) => (
                        <div key={idx} className="flex flex-col gap-2 flex-shrink-0">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-1">{category.group}</span>
                            <div className="flex bg-white rounded-xl shadow-sm border border-gray-200 p-1 gap-1">
                                {category.tabs.map(tab => {
                                    const IconComponent = tab.icon;
                                    return (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id)}
                                            className={`flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 font-bold text-[10px] sm:text-sm rounded-lg transition-all whitespace-nowrap flex-shrink-0 ${activeTab === tab.id ? 'bg-pink-50 text-pink-700 shadow-sm ring-1 ring-pink-200' : 'bg-transparent text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}
                                        >
                                            <span className={`${activeTab === tab.id ? 'text-pink-600' : 'text-gray-400'}`}>
                                                <IconComponent />
                                            </span> 
                                            <span>{tab.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            
            {/* --- TAB CONTENT: OVERVIEW --- */}
            <div className={activeTab === 'overview' ? 'block space-y-8 animate-fade-in' : 'hidden'}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                    <AnalyticsCard title="Revenue (This Month)" value={`$${analytics.monthlyRevenue}`} icon={"💰"} />
                    <AnalyticsCard title="Total Revenue" value={`$${analytics.totalRevenue}`} icon={"📈"} />
                    <AnalyticsCard title="Total Bookings" value={analytics.totalBookings} icon={"📦"} />
                    <AnalyticsCard title="Pending Requests" value={analytics.pendingRequests} icon={"🔔"} onClick={() => setActiveTab('orders')} />
                    <AnalyticsCard title="Most Popular Item" value={analytics.topListing} icon={"⭐"} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2">
                        <RevenueChart bookings={bookings} />
                    </div>
                    <div className="lg:col-span-1">
                         {needsActionBookings.length > 0 ? (
                            <div className="bg-white p-6 rounded-lg shadow-lg h-full border border-gray-100 flex flex-col">
                                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                                    <span className="relative flex h-3 w-3">
                                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                                      <span className="relative inline-flex rounded-full h-3 w-3 bg-yellow-500"></span>
                                    </span>
                                    Needs Action
                                </h3>
                                <div className="space-y-3 overflow-y-auto max-h-[300px] pr-2 custom-scrollbar">
                                    {needsActionBookings.map(booking => (
                                        <div key={booking.id} className="flex justify-between items-center p-3 bg-yellow-50 rounded-md border border-yellow-100 hover:shadow-sm transition cursor-pointer" onClick={() => handleViewBooking(booking.id)}>
                                            <div className="overflow-hidden">
                                                <p className="font-semibold text-gray-800 truncate">{booking.renterName}</p>
                                                <p className="text-xs text-gray-500 truncate">{booking.listingTitle}</p>
                                                <p className="text-xs text-yellow-700 font-medium">{booking.date}</p>
                                            </div>
                                            <button className="bg-white text-yellow-700 border border-yellow-200 px-3 py-1 rounded-full text-xs font-bold hover:bg-yellow-100 shadow-sm">View</button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white p-6 rounded-lg shadow-lg h-full flex flex-col items-center justify-center text-center border border-gray-100">
                                <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4 text-3xl">✅</div>
                                <h3 className="font-bold text-gray-800 text-lg">All Caught Up!</h3>
                                <p className="text-gray-500">No pending requests at the moment.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* --- TAB CONTENT: ORDERS & CALENDAR --- */}
            <div className={activeTab === 'orders' ? 'block space-y-8 animate-fade-in' : 'hidden'}>
                <VendorCalendar user={user} db={db} bookings={bookings} listings={listings} navigateTo={navigateTo} onPlanRouteForDay={(date) => { setLogisticsDate(date); setShowLogisticsPlanner(true); }} />

                <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
                    <div className="p-4 md:p-6 border-b border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-gray-50">
                        <div>
                            <h3 className="text-xl font-bold text-gray-900">Order Management</h3>
                            <p className="text-sm text-gray-500">Track and manage all your rental bookings.</p>
                        </div>
                        <div className="flex flex-wrap gap-2 items-center w-full md:w-auto">
                            <div className="relative flex-1 md:flex-none">
                                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">🔍</span>
                                <input type="text" placeholder="Search orders..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full md:w-64 pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none transition shadow-sm" />
                            </div>
                            <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="p-2 border border-gray-300 rounded-lg bg-white text-sm font-medium shadow-sm outline-none focus:ring-2 focus:ring-pink-500 cursor-pointer flex-1 md:flex-none">
                                <option value="newest">Newest First</option>
                                <option value="date-asc">Event Date (Soonest)</option>
                                <option value="total-desc">Highest Value</option>
                            </select>
                        </div>
                    </div>
                    
                    <div className="px-4 md:px-6 py-3 border-b border-gray-100 bg-white overflow-x-auto custom-scrollbar flex gap-2">
                        {[
                            { id: 'all', label: 'All Orders' },
                            { id: 'pending', label: 'Needs Approval', count: bookings.filter(b => b.status === 'pending').length },
                            { id: 'active', label: 'Active & Upcoming', count: bookings.filter(b => ['awaitingPayment', 'deposit-paid', 'booked', 'awaitingFinalPayment', 'awaitingPlannerConfirmation', 'delivered', 'awaitingPickup'].includes(b.status)).length },
                            { id: 'completed', label: 'Completed', count: bookings.filter(b => ['paid', 'returned', 'completed'].includes(b.status)).length },
                            { id: 'cancelled', label: 'Cancelled', count: bookings.filter(b => ['cancelled', 'declined'].includes(b.status)).length }
                        ].map(f => (
                            <button 
                                key={f.id}
                                onClick={() => setOrderStatusFilter(f.id)}
                                className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-bold transition flex items-center gap-2 ${orderStatusFilter === f.id ? 'bg-gray-900 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                            >
                                {f.label}
                                {f.count !== undefined && (
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] ${orderStatusFilter === f.id ? 'bg-white/20 text-white' : 'bg-white text-gray-500 shadow-sm'}`}>
                                        {f.count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                    
                    <div className="p-4 md:p-6 bg-gray-50/50">
                        <div className="space-y-4">
                            {filteredAndSortedBookings.length > 0 ? (
                                filteredAndSortedBookings.slice(0, visibleBookingCount).map(b => (
                                    <div key={b.id} id={b.id} className={`border rounded-xl overflow-hidden transition-all shadow-sm ${expandedBookingId === b.id ? 'border-pink-300 ring-1 ring-pink-500/20' : 'border-gray-200 hover:border-pink-300 hover:shadow-md'}`}>
                                        <div onClick={() => setExpandedBookingId(b.id === expandedBookingId ? null : b.id)} className={`p-4 bg-white cursor-pointer transition-colors flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 ${expandedBookingId === b.id ? 'bg-pink-50/30 border-b border-gray-200' : ''}`}>
                                            <div className="flex gap-4 items-center w-full sm:w-auto">
                                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 border shadow-sm ${b.status === 'pending' ? 'bg-yellow-50 border-yellow-200 text-yellow-600' : ['cancelled', 'declined'].includes(b.status) ? 'bg-red-50 border-red-200 text-red-600' : 'bg-blue-50 border-blue-200 text-blue-600'}`}>
                                                    <span className="text-xl">{b.status === 'pending' ? '🔔' : ['cancelled', 'declined'].includes(b.status) ? '❌' : '📦'}</span>
                                                </div>
                                                <div className="flex-grow min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="text-xs font-mono font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded">#{b.bookingNumber || b.id.slice(0,6).toUpperCase()}</span>
                                                        {b.renterId.startsWith('manual_') && <span className="bg-indigo-100 text-indigo-800 text-[10px] font-bold px-1.5 py-0.5 rounded">MANUAL</span>}
                                                        {b.status === 'pending' && <span className="bg-yellow-100 text-yellow-800 text-[10px] font-bold px-1.5 py-0.5 rounded animate-pulse">ACTION REQUIRED</span>}
                                                    </div>
                                                    <p className="font-bold text-gray-900 truncate text-base">{b.listingTitle} <span className="text-gray-500 font-normal text-sm ml-1">x{b.quantity}</span></p>
                                                    <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-gray-600">
                                                        <span className="font-medium text-gray-800">👤 {b.renterName || b.renterEmail}</span>
                                                        <span className="hidden sm:inline text-gray-300">•</span>
                                                        <span className="flex items-center gap-1">🗓️ {b.date} {b.date !== b.returnDate ? ` - ${b.returnDate}` : ''}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between w-full sm:w-auto gap-2 border-t sm:border-0 pt-3 sm:pt-0 border-gray-100">
                                                <span className="text-lg font-black text-gray-900">${(b.totalCost || 0).toFixed(2)}</span>
                                                <div className="flex flex-col items-end gap-1">
                                                    <span className={`px-3 py-1 text-[10px] font-bold rounded-full uppercase tracking-widest border shadow-sm ${getStatusClasses(b.status)}`}>
                                                        {b.status.replace(/([A-Z])/g, ' $1').trim()}
                                                    </span>
                                                    <button onClick={(e) => { e.stopPropagation(); setEditingOrder(b); }} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded text-[10px] font-bold transition border border-gray-200 shadow-sm mt-1">Edit / Cancel</button>
                                                </div>
                                            </div>
                                        </div>
                                        {expandedBookingId === b.id && (
                                            <BookingDetailsCard booking={b} onMarkAsDelivered={handleMarkAsDelivered} onOpenReturnModal={() => {setSelectedBooking(b); setShowReturnModal(true);}} onAcceptBooking={setBookingToAccept} onDeclineBooking={setBookingToDecline} onOpenInvoiceModal={() => setBookingToInvoice(b)} error={bookingErrors[b.id]} onRequestFinalPayment={handleRequestFinalPayment} onStartChat={handleStartChatFromBooking} onOpenContractModal={() => setBookingToContract(b)} />
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-16 bg-white rounded-xl border border-dashed border-gray-300">
                                    <div className="text-4xl mb-3">📋</div>
                                    <h3 className="text-lg font-bold text-gray-900">No orders found</h3>
                                    <p className="text-gray-500 text-sm mt-1">Try adjusting your filters or search term.</p>
                                </div>
                            )}
                            {filteredAndSortedBookings.length > visibleBookingCount && (
                                <div className="flex justify-center pt-4">
                                    <button onClick={() => setVisibleBookingCount(prev => Math.min(prev + 20, filteredAndSortedBookings.length))} className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-5 py-3 rounded-full font-semibold shadow-sm transition">
                                        Load more orders
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* --- TAB CONTENT: INVENTORY (Merged Listings + Stock + Maintenance) --- */}
            <div className={activeTab === 'listings' ? 'block space-y-6 animate-fade-in' : 'hidden'}>
                <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
                    <button onClick={() => setInventorySubTab('items')} className={`px-4 py-2 rounded-md text-sm font-medium transition ${inventorySubTab === 'items' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Manage Listings</button>
                    <button onClick={() => setInventorySubTab('stock')} className={`px-4 py-2 rounded-md text-sm font-medium transition ${inventorySubTab === 'stock' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Quick Stock Update</button>
                    <button onClick={() => setInventorySubTab('maintenance')} className={`px-4 py-2 rounded-md text-sm font-medium transition ${inventorySubTab === 'maintenance' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Maintenance Log</button>
                </div>

                {inventorySubTab === 'items' ? (
                    (showAddForm || editingListingId) ? (
                        <AddListingForm
                            user={user}
                            db={db}
                            storage={storage}
                            onComplete={() => { setShowAddForm(false); setEditingListingId(null); }}
                            initialData={editingListingId ? listings.find(l => l.id === editingListingId) : null}
                            isMapsScriptLoaded={isMapsScriptLoaded}
                        />
                    ) : (
                        <div className="space-y-4">
                            {listings.length > 0 && (
                                <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                                    <div className="flex items-center gap-2">
                                        <label className="text-sm font-semibold text-gray-700">Filter:</label>
                                        <select 
                                            value={listingCategoryFilter} 
                                            onChange={(e) => setListingCategoryFilter(e.target.value)} 
                                            className="p-2 border rounded-md text-sm capitalize bg-gray-50"
                                        >
                                            {uniqueCategories.map(cat => (
                                                <option key={cat} value={cat}>{cat === 'all' ? 'All Categories' : cat.replace('-', ' ')}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <span className="text-sm text-gray-500">Showing {filteredListings.length} items</span>
                                </div>
                            )}

                            <div className="bg-white p-4 rounded-lg shadow-lg space-y-3">
                                {filteredListings.length > 0 ? filteredListings.map(listing => (
                                    <div key={listing.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-md border border-gray-100 hover:border-gray-300 transition">
                                        <div className="flex items-center">
                                            <img src={listing.imageUrls?.[0] || 'https://placehold.co/64x64/e2e8f0/64748b?text=Img'} alt={listing.title} className="w-12 h-12 rounded-md object-cover mr-4 bg-gray-200" />
                                            <div>
                                                <p className="font-semibold text-gray-900">{listing.title}</p>
                                                <p className="text-sm text-gray-500 flex items-center gap-2">
                                                    <span className="font-medium text-green-600">${listing.price}/day</span>
                                                    <span className="text-gray-300">|</span>
                                                    <span>Stock: {listing.inventory}</span>
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex space-x-2">
                                            <button 
                                                onClick={() => handleToggleFeatured(listing)} 
                                                className={`px-3 py-1 rounded-lg text-sm font-semibold transition border ${listing.isFeatured ? 'bg-yellow-50 border-yellow-200 text-yellow-600 hover:bg-yellow-100' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                                                title={listing.isFeatured ? "Remove from Featured" : "Mark as Featured"}
                                            >
                                                {listing.isFeatured ? '★ Featured' : '☆ Feature'}
                                            </button>
                                            <button onClick={() => { setEditingListingId(listing.id); setShowAddForm(true); }} className="bg-white border border-blue-200 text-blue-600 px-3 py-1 rounded-lg text-sm font-semibold hover:bg-blue-50 transition">Edit</button>
                                            <button onClick={() => setListingToDelete(listing)} className="bg-white border border-red-200 text-red-600 px-3 py-1 rounded-lg text-sm font-semibold hover:bg-red-50 transition">Delete</button>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
                                        <p className="text-gray-500">No listings found{listingCategoryFilter !== 'all' ? ' in this category' : ''}.</p>
                                        {listings.length === 0 && <p className="text-sm mt-2 text-pink-600 font-semibold cursor-pointer" onClick={() => setShowAddForm(true)}>+ Add your first listing</p>}
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                ) : inventorySubTab === 'stock' ? (
                    <InventoryManagementTab listings={listings} db={db} />
                ) : (
                    <VendorMaintenanceTab user={user} db={db} listings={listings} />
                )}
            </div>

            {/* --- TAB CONTENT: CRM & LEADS --- */}
            <div className={activeTab === 'crm' ? 'block animate-fade-in h-full' : 'hidden'}>
                <VendorCRMTab user={user} db={db} />
            </div>

            {/* --- TAB CONTENT: MARKETING (Reviews + Coupons + Presentation) --- */}
            <div className={activeTab === 'marketing' ? 'block space-y-6 animate-fade-in' : 'hidden'}>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                    <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
                        <button onClick={() => setMarketingSubTab('reviews')} className={`px-4 py-2 rounded-md text-sm font-medium transition ${marketingSubTab === 'reviews' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Reviews</button>
                        <button onClick={() => setMarketingSubTab('coupons')} className={`px-4 py-2 rounded-md text-sm font-medium transition ${marketingSubTab === 'coupons' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Coupons</button>
                    </div>
                    <button onClick={() => setShowCardGenerator(true)} className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-5 py-2.5 rounded-lg text-sm font-bold shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition flex items-center gap-2 w-full sm:w-auto justify-center">
                        <span>🖨️</span> Print Promo Card
                    </button>
                </div>

                {marketingSubTab === 'reviews' ? (
                    <VendorReviewsTab user={user} db={db} reviews={reviews} />
                ) : (
                    <VendorCoupons user={user} db={db} />
                )}
            </div>

            {/* --- TAB CONTENT: FINANCE (Payouts + Expenses + Taxes) --- */}
            <div className={activeTab === 'finance' ? 'block animate-fade-in' : 'hidden'}>
                <VendorFinanceTab user={user} db={db} storage={storage} functions={functions} isUserAuthenticated={isUserAuthenticated} bookings={bookings} />
            </div>

        </div>
    );
}

// ==========================================
// Extracting required Vendor Dashboard Sub-Tabs
// ==========================================

function VendorReviewsTab({ user, db, reviews }) {
    const [replyingToId, setReplyingToId] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);

    const handleSubmitReply = async (reviewId) => {
        if (!replyText.trim()) return;
        try {
            await updateDoc(doc(db, "reviews", reviewId), {
                vendorReply: replyText,
                vendorReplyAt: serverTimestamp()
            });
            setReplyingToId(null);
            setReplyText('');
        } catch (error) {
            console.error("Error submitting reply:", error);
            alert("Failed to submit reply. Please try again.");
        }
    };

    const handleAiGenerateReply = async (review) => {
        setIsGenerating(true);
        try {
            const prompt = `You are a professional party rental vendor named ${user.vendorName || 'Vendor'}. Write a polite, professional, and warm response to the following customer review.
            Customer Name: ${review.renterName || 'Guest'}
            Rating: ${review.rating}/5 stars
            Review Text: "${review.text}"
            Guidelines:
            - If the rating is 5 stars, thank them warmly and invite them back.
            - If the rating is low (1-3 stars), apologize professionally and offer to make it right offline.
            - Keep it brief (2-3 sentences max).
            - Do not include placeholders like [Your Name].`;

            const payload = { contents: [{ role: "user", parts: [{ text: prompt }] }] };
            // Note: Assuming GEMINI_API_KEY is available globally as defined in config
            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${window.GEMINI_API_KEY || ''}`;
            
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            if (result.candidates?.[0]) {
                setReplyText(result.candidates[0].content.parts[0].text.trim());
            } else {
                throw new Error("No response from AI");
            }
        } catch (err) {
            console.error("AI Reply Error:", err);
            alert("Could not generate reply. Please try again.");
        } finally {
            setIsGenerating(false);
        }
    };

    if (!reviews) return <div className="p-8 text-center text-gray-500">Loading reviews...</div>;

    if (reviews.length === 0) {
        return (
            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                <div className="w-16 h-16 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">⭐</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">No Reviews Yet</h3>
                <p className="text-gray-600">Reviews will appear here once your customers leave feedback on your completed bookings.</p>
            </div>
        );
    }

    const averageRating = (reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1);

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-lg flex items-center">
                    <div className="mr-4 text-4xl font-bold text-gray-900">{averageRating}</div>
                    <div>
                        <div className="flex text-yellow-400 text-lg mb-1">
                            {[...Array(5)].map((_, i) => (
                                <span key={i}>{i < Math.round(averageRating) ? '★' : '☆'}</span>
                            ))}
                        </div>
                        <p className="text-sm text-gray-500">Average Rating</p>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg flex items-center">
                    <div className="mr-4 text-4xl font-bold text-gray-900">{reviews.length}</div>
                    <div>
                        <p className="font-semibold text-gray-800">Total Reviews</p>
                        <p className="text-sm text-gray-500">Lifetime</p>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
                    <h3 className="font-bold text-gray-800">Recent Feedback</h3>
                </div>
                <div className="divide-y divide-gray-100">
                    {reviews.map(review => (
                        <div key={review.id} className="p-6 hover:bg-gray-50 transition">
                            <div className="flex justify-between items-start mb-2">
                                <div className="flex items-center gap-2">
                                    <div className="font-semibold text-gray-900">{review.renterName || 'Guest'}</div>
                                    <span className="text-gray-300">•</span>
                                    <span className="text-xs text-gray-500">{review.createdAt ? new Date(review.createdAt.seconds * 1000).toLocaleDateString() : 'Recent'}</span>
                                </div>
                                <div className="flex text-yellow-400 text-sm">
                                    {[...Array(5)].map((_, i) => (
                                        <span key={i}>{i < review.rating ? '★' : '☆'}</span>
                                    ))}
                                </div>
                            </div>
                            <p className="text-gray-600 leading-relaxed italic">"{review.text}"</p>
                            <p className="mt-3 text-xs text-blue-600 font-medium bg-blue-50 inline-block px-2 py-1 rounded">
                                Item: {review.listingId || 'Rental Service'}
                            </p>

                            {review.vendorReply ? (
                                <div className="mt-4 ml-4 p-3 bg-gray-100 rounded-lg border-l-4 border-pink-500">
                                    <p className="text-xs font-bold text-gray-700 mb-1">Response from You</p>
                                    <p className="text-sm text-gray-600">{review.vendorReply}</p>
                                </div>
                            ) : (
                                <div className="mt-3">
                                    {replyingToId === review.id ? (
                                        <div className="mt-2 animate-fade-in bg-gray-50 p-3 rounded-lg border border-gray-200">
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-xs font-semibold text-gray-500">Your Response:</span>
                                                <button 
                                                    onClick={() => handleAiGenerateReply(review)} 
                                                    disabled={isGenerating}
                                                    className="text-xs flex items-center gap-1 bg-purple-100 text-purple-700 px-2 py-1 rounded hover:bg-purple-200 disabled:opacity-50 transition"
                                                >
                                                    {isGenerating ? (
                                                        <><span>Writing...</span><span className="animate-spin h-3 w-3 border-2 border-purple-600 border-t-transparent rounded-full"></span></>
                                                    ) : (
                                                        <><span>✨ Auto-Draft</span></>
                                                    )}
                                                </button>
                                            </div>
                                            <textarea
                                                value={replyText}
                                                onChange={(e) => setReplyText(e.target.value)}
                                                placeholder="Write your response..."
                                                className="w-full p-2 border rounded-md text-sm mb-2 focus:ring-2 focus:ring-pink-500 outline-none"
                                                rows="3"
                                            />
                                            <div className="flex gap-2">
                                                <button onClick={() => handleSubmitReply(review.id)} className="bg-pink-500 text-white px-3 py-1 rounded text-xs font-bold hover:bg-pink-600">Post Reply</button>
                                                <button onClick={() => { setReplyingToId(null); setReplyText(''); }} className="bg-gray-200 text-gray-700 px-3 py-1 rounded text-xs font-bold hover:bg-gray-300">Cancel</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <button onClick={() => { setReplyingToId(review.id); setReplyText(''); }} className="text-xs text-pink-600 font-bold hover:underline flex items-center gap-1">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
                                            Reply to Review
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function VendorCoupons({ user, db }) {
    const [coupons, setCoupons] = useState(user.coupons || []);
    const [newCode, setNewCode] = useState('');
    const [newAmount, setNewAmount] = useState('');
    const [newType, setNewType] = useState('percent');
    const [loading, setLoading] = useState(false);

    const handleAddCoupon = async () => {
        if (!newCode || !newAmount) return;
        setLoading(true);
        const updatedCoupons = [...coupons, { 
            code: newCode.toUpperCase().trim(), 
            amount: parseFloat(newAmount), 
            type: newType,
            createdAt: new Date().toISOString()
        }];
        
        try {
            await updateDoc(doc(db, "users", user.uid), { coupons: updatedCoupons });
            setCoupons(updatedCoupons);
            setNewCode('');
            setNewAmount('');
        } catch(err) {
            console.error("Error adding coupon", err);
            alert("Failed to add coupon");
        }
        setLoading(false);
    };

    const handleDelete = async (index) => {
        if (!window.confirm("Are you sure you want to delete this coupon?")) return;
        const updatedCoupons = coupons.filter((_, i) => i !== index);
        try {
            await updateDoc(doc(db, "users", user.uid), { coupons: updatedCoupons });
            setCoupons(updatedCoupons);
        } catch (err) {
            console.error("Error deleting coupon", err);
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-bold mb-4">Manage Coupons</h3>
            <div className="flex flex-col md:flex-row gap-4 mb-6 items-end bg-gray-50 p-4 rounded-lg">
                <div className="w-full md:w-auto">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Coupon Code</label>
                    <input type="text" value={newCode} onChange={e => setNewCode(e.target.value.toUpperCase())} className="p-2 border rounded uppercase w-full font-mono" placeholder="SUMMER10" />
                </div>
                <div className="w-full md:w-auto">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Discount Amount</label>
                    <div className="flex">
                        <input type="number" value={newAmount} onChange={e => setNewAmount(e.target.value)} className="p-2 border rounded-l w-24" placeholder="10" />
                        <select value={newType} onChange={e => setNewType(e.target.value)} className="p-2 border rounded-r bg-gray-100 border-l-0">
                            <option value="percent">%</option>
                            <option value="fixed">$</option>
                        </select>
                    </div>
                </div>
                <button onClick={handleAddCoupon} disabled={loading || !newCode || !newAmount} className="w-full md:w-auto bg-pink-500 text-white px-6 py-2 rounded font-semibold hover:bg-pink-600 disabled:bg-gray-300 disabled:cursor-not-allowed h-[42px]">
                    {loading ? 'Adding...' : 'Add Coupon'}
                </button>
            </div>
            
            <div className="space-y-3">
                {coupons.length > 0 ? coupons.map((coupon, idx) => (
                    <div key={idx} className="flex justify-between items-center p-4 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow">
                        <div className="flex items-center gap-3">
                            <span className="bg-green-100 text-green-800 font-mono font-bold px-3 py-1 rounded">{coupon.code}</span>
                            <span className="text-gray-700 font-medium">{coupon.type === 'percent' ? `${coupon.amount}% off` : `$${coupon.amount.toFixed(2)} off`}</span>
                        </div>
                        <button onClick={() => handleDelete(idx)} className="text-red-500 hover:text-red-700 text-sm font-semibold px-3 py-1 rounded hover:bg-red-50">Delete</button>
                    </div>
                )) : (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                        No active coupons. Create one to offer discounts to your renters!
                    </div>
                )}
            </div>
        </div>
    );
}

function VendorOrderEditModal({ booking, onClose, db }) {
    const [status, setStatus] = useState(booking?.status || 'pending');
    const [startDate, setStartDate] = useState(booking?.date || '');
    const [endDate, setEndDate] = useState(booking?.returnDate || '');
    const [notes, setNotes] = useState(booking?.notes || '');
    const [isSaving, setIsSaving] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);

    if (!booking) return null;

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const bookingRef = doc(db, 'bookings', booking.id);
            await updateDoc(bookingRef, {
                status,
                date: startDate,
                returnDate: endDate,
                notes: notes,
                updatedAt: serverTimestamp()
            });
            
            if (booking.renterId && !booking.renterId.startsWith('manual_') && booking.status !== status) {
                await addDoc(collection(db, 'notifications'), {
                    userId: booking.renterId,
                    message: `Your order for ${booking.listingTitle} has been updated to: ${status}`,
                    isRead: false,
                    createdAt: serverTimestamp(),
                    destination: { page: 'bookingDetail', data: { booking: { id: booking.id } } }
                });
            }
            onClose();
        } catch (error) {
            console.error("Error updating booking:", error);
        } finally {
            setIsSaving(false);
        }
    };

    const executeCancel = async () => {
        setIsSaving(true);
        try {
            const bookingRef = doc(db, 'bookings', booking.id);
            await updateDoc(bookingRef, {
                status: 'cancelled',
                updatedAt: serverTimestamp()
            });

            if (booking.renterId && !booking.renterId.startsWith('manual_')) {
                await addDoc(collection(db, 'notifications'), {
                    userId: booking.renterId,
                    message: `Your order for ${booking.listingTitle} has been cancelled by the vendor.`,
                    isRead: false,
                    createdAt: serverTimestamp(),
                    destination: { page: 'bookingDetail', data: { booking: { id: booking.id } } }
                });
            }
            onClose();
        } catch (error) {
            console.error("Error cancelling booking:", error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-2xl max-w-lg w-full p-6 md:p-8 shadow-2xl overflow-y-auto max-h-[90vh]">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold text-gray-900">Edit Order: #{booking.bookingNumber || booking.id.slice(0,6)}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors text-2xl leading-none">&times;</button>
                </div>

                {showCancelConfirm ? (
                    <div className="bg-red-50 text-red-700 p-6 rounded-xl mb-6 border border-red-100">
                        <h3 className="font-bold text-lg mb-2">Cancel Order?</h3>
                        <p className="mb-4 text-sm">This action cannot be undone. The customer will be notified.</p>
                        <div className="flex gap-3">
                            <button onClick={executeCancel} disabled={isSaving} className="bg-red-600 text-white px-4 py-2.5 rounded-lg font-bold hover:bg-red-700 transition flex-1 shadow-sm disabled:opacity-50">
                                {isSaving ? 'Cancelling...' : 'Yes, Cancel Order'}
                            </button>
                            <button onClick={() => setShowCancelConfirm(false)} disabled={isSaving} className="bg-white text-gray-700 px-4 py-2.5 rounded-lg font-bold border border-gray-300 hover:bg-gray-50 transition flex-1 shadow-sm">
                                Keep Order
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-5">
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Status</label>
                            <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-pink-500 outline-none bg-white">
                                <option value="pending">Pending</option>
                                <option value="awaitingPayment">Awaiting Payment</option>
                                <option value="deposit-paid">Deposit Paid</option>
                                <option value="paid">Paid (Confirmed)</option>
                                <option value="delivered">Delivered</option>
                                <option value="returned">Returned</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                            </select>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Start Date</label>
                                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-pink-500 outline-none" />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">End Date</label>
                                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-pink-500 outline-none" />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Order Notes (Internal)</label>
                            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-pink-500 outline-none h-24 resize-none" placeholder="Add any internal notes about this order..." />
                        </div>

                        <div className="pt-4 flex justify-between items-center border-t border-gray-100 mt-2">
                            <button onClick={() => setShowCancelConfirm(true)} className="text-red-500 font-bold text-sm hover:text-red-700 px-3 py-2 rounded-lg hover:bg-red-50 transition">Cancel Order</button>
                            <div className="flex gap-3">
                                <button onClick={onClose} className="px-5 py-2.5 rounded-lg font-bold text-gray-600 hover:bg-gray-100 transition border border-gray-200">Close</button>
                                <button onClick={handleSave} disabled={isSaving} className="bg-gray-900 text-white px-5 py-2.5 rounded-lg font-bold hover:bg-black transition shadow-md disabled:opacity-50">
                                    {isSaving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function VendorCRMTab({ user, db }) {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);

    const stages = [
        { id: 'Lead', label: 'New Lead', color: 'bg-blue-100 border-blue-200 text-blue-800' },
        { id: 'Contacted', label: 'Contacted', color: 'bg-yellow-100 border-yellow-200 text-yellow-800' },
        { id: 'Negotiation', label: 'In Negotiation', color: 'bg-purple-100 border-purple-200 text-purple-800' },
        { id: 'Booked', label: 'Booked', color: 'bg-green-100 border-green-200 text-green-800' },
        { id: 'Lost', label: 'Lost', color: 'bg-red-100 border-red-200 text-red-800' }
    ];

    useEffect(() => {
        if (!user || !user.uid) return;
        const q = query(collection(db, `users/${user.uid}/crm_clients`), orderBy('updatedAt', 'desc'));
        const unsub = onSnapshot(q, (snapshot) => {
            setClients(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });
        return () => unsub();
    }, [user, db]);

    const handleDragStart = (e, clientId) => {
        e.dataTransfer.setData('clientId', clientId);
    };

    const handleDrop = async (e, newStatus) => {
        e.preventDefault();
        const clientId = e.dataTransfer.getData('clientId');
        if (clientId) {
            const client = clients.find(c => c.id === clientId);
            if (client && client.status !== newStatus) {
                await updateDoc(doc(db, `users/${user.uid}/crm_clients`, clientId), {
                    status: newStatus,
                    updatedAt: serverTimestamp()
                });
            }
        }
    };

    const handleDragOver = (e) => { e.preventDefault(); };

    const openModal = (client = null) => {
        setSelectedClient(client);
        setIsModalOpen(true);
    };

    if (loading) return <div className="p-8 text-center text-gray-500">Loading CRM...</div>;

    const totalValue = clients.filter(c => c.status === 'Booked').reduce((acc, c) => acc + (parseFloat(c.value) || 0), 0);
    const pipelineValue = clients.filter(c => ['Lead', 'Contacted', 'Negotiation'].includes(c.status)).reduce((acc, c) => acc + (parseFloat(c.value) || 0), 0);

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div>
                    <h3 className="text-xl font-bold text-gray-900">Client Pipeline</h3>
                    <div className="flex gap-4 mt-1 text-sm">
                        <span className="text-green-600 font-semibold">Booked Value: ${totalValue.toLocaleString()}</span>
                        <span className="text-blue-600 font-semibold">Pipeline Value: ${pipelineValue.toLocaleString()}</span>
                    </div>
                </div>
                <button onClick={() => openModal()} className="bg-pink-600 text-white px-5 py-2.5 rounded-lg font-bold shadow-md hover:bg-pink-700 transition flex items-center gap-2">
                    <span>+</span> Add Lead
                </button>
            </div>

            <div className="flex overflow-x-auto gap-4 pb-4 custom-scrollbar min-h-[500px]">
                {stages.map(stage => {
                    const stageClients = clients.filter(c => c.status === stage.id);
                    return (
                        <div 
                            key={stage.id} 
                            className="bg-gray-50 rounded-xl border border-gray-200 flex-shrink-0 w-72 flex flex-col"
                            onDrop={(e) => handleDrop(e, stage.id)}
                            onDragOver={handleDragOver}
                        >
                            <div className={`px-4 py-3 border-b border-gray-200 rounded-t-xl font-bold flex justify-between items-center ${stage.color}`}>
                                <span>{stage.label}</span>
                                <span className="bg-white/50 text-gray-800 px-2 py-0.5 rounded-full text-xs">{stageClients.length}</span>
                            </div>
                            <div className="flex-1 p-3 space-y-3 overflow-y-auto">
                                {stageClients.map(client => (
                                    <div 
                                        key={client.id}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, client.id)}
                                        onClick={() => openModal(client)}
                                        className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 cursor-grab active:cursor-grabbing hover:border-pink-300 hover:shadow-md transition-all group"
                                    >
                                        <div className="flex justify-between items-start mb-1">
                                            <h4 className="font-bold text-gray-900 truncate pr-2 group-hover:text-pink-600 transition-colors">{client.name}</h4>
                                            {client.value > 0 && <span className="text-xs font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded">${client.value}</span>}
                                        </div>
                                        {client.eventDate && <p className="text-xs text-gray-500 mb-2 flex items-center gap-1"><span>📅</span> {new Date(client.eventDate).toLocaleDateString()}</p>}
                                        {(client.email || client.phone) && (
                                            <div className="text-xs text-gray-400 space-y-0.5 truncate">
                                                {client.email && <p>✉️ <a href={`mailto:${client.email}`} className="hover:text-pink-500 hover:underline transition" onClick={(e) => e.stopPropagation()}>{client.email}</a></p>}
                                                {client.phone && <p onClick={(e) => e.stopPropagation()}>📞 <a href={`tel:${client.phone}`} className="hover:text-pink-600 hover:underline transition">{client.phone}</a></p>}
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {stageClients.length === 0 && (
                                    <div className="h-full flex items-center justify-center text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-lg py-8 opacity-50">
                                        Drop here
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {isModalOpen && (
                <CrmClientModal user={user} db={db} client={selectedClient} onClose={() => setIsModalOpen(false)} />
            )}
        </div>
    );
}

function VendorTaxReports({ user, db, bookings }) {
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    
    const taxBookings = useMemo(() => {
        return bookings.filter(b => {
            if (['cancelled', 'declined', 'pending'].includes(b.status)) return false;
            const d = new Date(b.date);
            return d.getFullYear() === parseInt(selectedYear);
        });
    }, [bookings, selectedYear]);

    const reportData = useMemo(() => {
        const monthlyData = Array(12).fill(0).map((_, i) => ({
            month: new Date(0, i).toLocaleString('default', { month: 'long' }),
            grossSales: 0,
            taxableSales: 0,
            taxCollected: 0,
            bookingsCount: 0
        }));

        taxBookings.forEach(b => {
            const month = new Date(b.date).getMonth();
            const tax = parseFloat(b.taxAmount || 0);
            const total = parseFloat(b.totalCost || 0);
            const taxable = total - tax; 

            monthlyData[month].grossSales += total;
            monthlyData[month].taxableSales += taxable;
            monthlyData[month].taxCollected += tax;
            monthlyData[month].bookingsCount += 1;
        });

        return monthlyData;
    }, [taxBookings]);

    const totals = reportData.reduce((acc, curr) => ({
        grossSales: acc.grossSales + curr.grossSales,
        taxableSales: acc.taxableSales + curr.taxableSales,
        taxCollected: acc.taxCollected + curr.taxCollected
    }), { grossSales: 0, taxableSales: 0, taxCollected: 0 });

    const exportCSV = () => {
        const headers = ["Month", "Bookings", "Gross Sales", "Taxable Sales", "Tax Collected"];
        const rows = reportData.map(d => [
            d.month, d.bookingsCount, d.grossSales.toFixed(2), d.taxableSales.toFixed(2), d.taxCollected.toFixed(2)
        ]);
        rows.push(["TOTAL", taxBookings.length, totals.grossSales.toFixed(2), totals.taxableSales.toFixed(2), totals.taxCollected.toFixed(2)]);
        const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', `Tax_Report_${selectedYear}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                <h3 className="text-lg font-bold text-gray-800">Sales Tax Liability Report</h3>
                <div className="flex gap-2">
                    <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} className="p-2 border rounded-md text-sm bg-gray-50 focus:ring-pink-500">
                        {[2023, 2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <button onClick={exportCSV} className="flex items-center gap-2 bg-blue-50 text-blue-600 px-4 py-2 rounded-md font-semibold text-sm hover:bg-blue-100 transition">
                        <span>⬇️</span> Download CSV
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                    <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Gross Sales</p>
                    <p className="text-2xl font-extrabold text-gray-900 mt-1">${totals.grossSales.toFixed(2)}</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                    <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Taxable Sales (Est)</p>
                    <p className="text-2xl font-extrabold text-gray-900 mt-1">${totals.taxableSales.toFixed(2)}</p>
                </div>
                <div className="bg-gradient-to-br from-pink-50 to-white p-6 rounded-lg shadow-sm border border-pink-100">
                    <p className="text-xs text-pink-700 font-bold uppercase tracking-wider">Total Tax Collected</p>
                    <p className="text-2xl font-extrabold text-pink-700 mt-1">${totals.taxCollected.toFixed(2)}</p>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-100">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 text-gray-500 font-semibold border-b">
                            <tr>
                                <th className="p-4">Month</th>
                                <th className="p-4 text-center">Bookings</th>
                                <th className="p-4 text-right">Gross Sales</th>
                                <th className="p-4 text-right">Taxable Sales</th>
                                <th className="p-4 text-right">Tax Collected</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {reportData.map((row, idx) => (
                                <tr key={idx} className="hover:bg-gray-50 transition">
                                    <td className="p-4 font-medium text-gray-800">{row.month}</td>
                                    <td className="p-4 text-center text-gray-600">{row.bookingsCount}</td>
                                    <td className="p-4 text-right text-gray-600">${row.grossSales.toFixed(2)}</td>
                                    <td className="p-4 text-right text-gray-600">${row.taxableSales.toFixed(2)}</td>
                                    <td className="p-4 text-right font-bold text-gray-800">${row.taxCollected.toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot className="bg-gray-50 font-bold text-gray-900 border-t-2 border-gray-200">
                            <tr>
                                <td className="p-4">TOTAL</td>
                                <td className="p-4 text-center">{taxBookings.length}</td>
                                <td className="p-4 text-right">${totals.grossSales.toFixed(2)}</td>
                                <td className="p-4 text-right">${totals.taxableSales.toFixed(2)}</td>
                                <td className="p-4 text-right">${totals.taxCollected.toFixed(2)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
            <p className="text-xs text-gray-400 italic text-center">
                * Disclaimer: This report is generated based on your transaction history and collected tax amounts. It is for informational purposes only and does not constitute official tax advice or filings.
            </p>
        </div>
    );
}

function VendorExpenses({ user, db, storage, bookings }) {
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newExpense, setNewExpense] = useState({ description: '', amount: '', category: 'Inventory', date: new Date().toISOString().split('T')[0] });
    const [receiptFile, setReceiptFile] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
    const toast = useToast();
    
    useEffect(() => {
        const q = query(collection(db, `users/${user.uid}/expenses`));
        const unsub = onSnapshot(q, (snap) => {
            const data = snap.docs.map(d => ({id: d.id, ...d.data()}));
            data.sort((a, b) => new Date(b.date) - new Date(a.date));
            setExpenses(data);
            setLoading(false);
        });
        return () => unsub();
    }, [user, db]);

    const handleAdd = async () => {
        if(!newExpense.description || !newExpense.amount) return;
        setIsSaving(true);
        try {
            let receiptUrl = null;
            if (receiptFile && storage) {
                const fileExt = receiptFile.name.split('.').pop();
                const filePath = `users/${user.uid}/receipts/${Date.now()}_receipt.${fileExt}`;
                const fileRef = ref(storage, filePath);
                await uploadBytes(fileRef, receiptFile);
                receiptUrl = await getDownloadURL(fileRef);
            }
            await addDoc(collection(db, `users/${user.uid}/expenses`), {
                ...newExpense,
                amount: parseFloat(newExpense.amount),
                receiptUrl,
                createdAt: serverTimestamp()
            });
            setNewExpense({ description: '', amount: '', category: 'Inventory', date: new Date().toISOString().split('T')[0] });
            setReceiptFile(null);
            toast.success('Expense logged successfully!');
        } catch (error) {
            console.error("Error adding expense:", error);
            toast.error('Failed to log expense.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if(!window.confirm("Delete this expense record?")) return;
        await deleteDoc(doc(db, `users/${user.uid}/expenses`, id));
    };

    const exportData = (data, filename) => {
        if (!data || data.length === 0) return alert("No data to export.");
        const flatten = (obj) => {
            const result = {};
            for (const key in obj) {
                if (typeof obj[key] === 'object' && obj[key] !== null && !obj[key].seconds) {
                    result[key] = JSON.stringify(obj[key]);
                } else if (obj[key] && obj[key].seconds) {
                    result[key] = new Date(obj[key].seconds * 1000).toLocaleDateString();
                } else {
                    result[key] = obj[key];
                }
            }
            return result;
        };

        const flatData = data.map(flatten);
        const headersSet = new Set();
        flatData.forEach(row => Object.keys(row).forEach(k => headersSet.add(k)));
        const headers = Array.from(headersSet);
        
        const csvContent = [
            headers.join(','),
            ...flatData.map(row => headers.map(fieldName => {
                let val = row[fieldName];
                if (val === undefined || val === null) val = '';
                if (typeof val === 'string') val = `"${val.replace(/"/g, '""')}"`;
                return val;
            }).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const filteredBookings = bookings.filter(b => {
        if (b.status === 'cancelled' || b.status === 'declined') return false;
        if (filterYear === 'all') return true;
        return new Date(b.date).getFullYear().toString() === filterYear;
    });

    const filteredExpenses = expenses.filter(e => {
        if (filterYear === 'all') return true;
        return new Date(e.date).getFullYear().toString() === filterYear;
    });

    const totalRevenue = filteredBookings.reduce((acc, b) => acc + (b.totalCost || 0) - (b.renterFee || 0), 0);
    const totalExpenses = filteredExpenses.reduce((acc, e) => acc + (e.amount || 0), 0);
    const netProfit = totalRevenue - totalExpenses;

    const expensesByCategory = filteredExpenses.reduce((acc, e) => {
        acc[e.category] = (acc[e.category] || 0) + e.amount;
        return acc;
    }, {});

    if (loading) return <div className="p-8 text-center">Loading financials...</div>;

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-wrap gap-4 justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-gray-700 uppercase tracking-wide">Financial Year:</span>
                    <select value={filterYear} onChange={e => setFilterYear(e.target.value)} className="p-2 border border-gray-300 rounded-lg text-sm font-bold text-pink-700 bg-pink-50 focus:ring-2 focus:ring-pink-500 outline-none cursor-pointer shadow-sm">
                        <option value="all">All Time</option>
                        {[2023, 2024, 2025, 2026].map(y => <option key={y} value={y.toString()}>{y}</option>)}
                    </select>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => exportData(filteredBookings, `bookings_${filterYear}.csv`)} className="text-sm flex items-center gap-2 text-gray-600 hover:text-pink-600 font-semibold bg-white border border-gray-200 px-3 py-2 rounded-lg shadow-sm transition">
                        <span>⬇️</span> Export Bookings
                    </button>
                    <button onClick={() => exportData(filteredExpenses, `expenses_${filterYear}.csv`)} className="text-sm flex items-center gap-2 text-gray-600 hover:text-pink-600 font-semibold bg-white border border-gray-200 px-3 py-2 rounded-lg shadow-sm transition">
                        <span>⬇️</span> Export Expenses
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <AnalyticsCard title="Gross Revenue" value={`$${totalRevenue.toFixed(2)}`} icon="💰" />
                <AnalyticsCard title="Total Expenses" value={`$${totalExpenses.toFixed(2)}`} icon="💸" />
                <div className={`bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center transition-all duration-300`}>
                    <div className={`rounded-full h-14 w-14 flex items-center justify-center text-2xl flex-shrink-0 ${netProfit >= 0 ? 'bg-green-50 text-green-500' : 'bg-red-50 text-red-500'}`}>
                        {netProfit >= 0 ? '📈' : '📉'}
                    </div>
                    <div className="ml-5 min-w-0">
                        <p className="text-xs text-gray-500 font-bold uppercase tracking-wide mb-1 truncate">Net Profit</p>
                        <p className={`text-2xl font-extrabold truncate ${netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`} title={`$${netProfit.toFixed(2)}`}>${netProfit.toFixed(2)}</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                        <h3 className="text-lg font-bold mb-4 text-gray-800 border-b pb-2">Log New Expense</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 mb-1">Date</label>
                                <input type="date" value={newExpense.date} onChange={e => setNewExpense({...newExpense, date: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none text-sm" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 mb-1">Description</label>
                                <input type="text" placeholder="e.g. New Tents, Gas" value={newExpense.description} onChange={e => setNewExpense({...newExpense, description: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none text-sm" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 mb-1">Amount ($)</label>
                                    <input type="number" placeholder="0.00" value={newExpense.amount} onChange={e => setNewExpense({...newExpense, amount: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none text-sm" />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 mb-1">Category</label>
                                    <select value={newExpense.category} onChange={e => setNewExpense({...newExpense, category: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-pink-500 outline-none text-sm">
                                        <option>Inventory</option>
                                        <option>Maintenance</option>
                                        <option>Marketing</option>
                                        <option>Delivery/Gas</option>
                                        <option>Office</option>
                                        <option>Contractors</option>
                                        <option>Software/Fees</option>
                                        <option>Other</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 mb-1">Attach Receipt (Optional)</label>
                                <div className="flex items-center gap-2">
                                    <input type="file" id="receiptUpload" accept="image/*,.pdf" onChange={e => setReceiptFile(e.target.files[0])} className="hidden" />
                                    <label htmlFor="receiptUpload" className="cursor-pointer flex-grow text-center bg-gray-50 border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-100 transition truncate px-2">
                                        {receiptFile ? `📄 ${receiptFile.name}` : '📁 Choose File'}
                                    </label>
                                    {receiptFile && <button onClick={() => setReceiptFile(null)} className="text-red-500 p-2 hover:bg-red-50 rounded-lg">✕</button>}
                                </div>
                            </div>
                            <button onClick={handleAdd} disabled={isSaving || !newExpense.description || !newExpense.amount} className="w-full bg-pink-600 text-white py-3 rounded-lg font-bold hover:bg-pink-700 transition shadow-md disabled:bg-gray-300 disabled:cursor-not-allowed mt-2">
                                {isSaving ? 'Uploading & Saving...' : 'Add Expense'}
                            </button>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                        <h3 className="text-lg font-bold mb-4 text-gray-800 border-b pb-2">Category Breakdown</h3>
                        <div className="space-y-4">
                            {Object.keys(expensesByCategory).length > 0 ? (
                                Object.entries(expensesByCategory).sort((a,b) => b[1] - a[1]).map(([cat, amt]) => {
                                    const percentage = totalExpenses > 0 ? (amt / totalExpenses) * 100 : 0;
                                    return (
                                        <div key={cat} className="group">
                                            <div className="flex justify-between text-sm mb-1.5">
                                                <span className="text-gray-600 font-semibold">{cat}</span>
                                                <span className="font-bold text-gray-900">${amt.toFixed(2)} <span className="text-[10px] text-gray-400 font-normal ml-1">({percentage.toFixed(0)}%)</span></span>
                                            </div>
                                            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden shadow-inner">
                                                <div className="bg-gradient-to-r from-pink-400 to-pink-500 h-full rounded-full transition-all duration-1000 ease-out" style={{ width: `${percentage}%` }}></div>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <p className="text-sm text-gray-500 italic text-center py-4">No expenses recorded for this period.</p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-lg border border-gray-100 flex flex-col h-full max-h-[800px]">
                    <h3 className="text-lg font-bold mb-4 text-gray-800 border-b pb-2">Expense Ledger</h3>
                    <div className="overflow-y-auto flex-grow pr-2 custom-scrollbar">
                        {filteredExpenses.length > 0 ? (
                            <table className="w-full text-sm text-left border-collapse">
                                <thead className="bg-gray-50 text-gray-500 sticky top-0 z-10 shadow-sm">
                                    <tr>
                                        <th className="p-3 font-semibold rounded-tl-md">Date</th>
                                        <th className="p-3 font-semibold">Description</th>
                                        <th className="p-3 font-semibold">Category</th>
                                        <th className="p-3 font-semibold text-center">Receipt</th>
                                        <th className="p-3 font-semibold text-right">Amount</th>
                                        <th className="p-3 rounded-tr-md"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {filteredExpenses.map(e => (
                                        <tr key={e.id} className="hover:bg-gray-50 transition group">
                                            <td className="p-3 text-gray-600 whitespace-nowrap">{new Date(e.date).toLocaleDateString()}</td>
                                            <td className="p-3 font-medium text-gray-900">{e.description}</td>
                                            <td className="p-3"><span className="bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold">{e.category}</span></td>
                                            <td className="p-3 text-center">
                                                {e.receiptUrl ? (
                                                    <a href={e.receiptUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 bg-blue-50 p-1.5 rounded inline-block transition" title="View Receipt">📄</a>
                                                ) : (
                                                    <span className="text-gray-300" title="No receipt attached">-</span>
                                                )}
                                            </td>
                                            <td className="p-3 text-right font-bold text-red-600">-${e.amount.toFixed(2)}</td>
                                            <td className="p-3 text-right opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button onClick={() => handleDelete(e.id)} className="text-gray-400 hover:text-red-500 transition font-bold p-1 rounded-full hover:bg-red-50" title="Delete record">✕</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-16 text-gray-400 border-2 border-dashed border-gray-100 rounded-xl bg-gray-50/50 mt-4">
                                <div className="text-4xl mb-3 opacity-50">🧾</div>
                                <p className="font-semibold text-gray-600">No expenses found.</p>
                                <p className="text-sm mt-1">Add your costs to calculate your true net profit.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function VendorMaintenanceTab({ user, db, listings }) {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newLog, setNewLog] = useState({ listingId: '', issue: '', cost: '', status: 'Broken' });
    const toast = useToast(); 

    useEffect(() => {
        if (!user || !db) return;
        const q = query(collection(db, `users/${user.uid}/maintenance`), orderBy('createdAt', 'desc'));
        const unsub = onSnapshot(q, snap => {
            setLogs(snap.docs.map(d => ({id: d.id, ...d.data()})));
            setLoading(false);
        }, (err) => {
            console.error("Error fetching maintenance logs:", err);
            setLoading(false);
        });
        return () => unsub();
    }, [user, db]);

    const handleAddLog = async (e) => {
        e.preventDefault();
        if (!newLog.listingId || !newLog.issue) return;
        const listing = listings.find(l => l.id === newLog.listingId);
        try {
            await addDoc(collection(db, `users/${user.uid}/maintenance`), {
                ...newLog,
                listingTitle: listing?.title || 'Unknown Item',
                cost: parseFloat(newLog.cost) || 0,
                dateLogged: new Date().toISOString(),
                createdAt: serverTimestamp()
            });
            setNewLog({ listingId: '', issue: '', cost: '', status: 'Broken' });
        } catch (err) {
            console.error(err);
            alert("Failed to add maintenance log.");
        }
    };

    const updateStatus = async (id, status) => {
        await updateDoc(doc(db, `users/${user.uid}/maintenance`, id), { status, dateUpdated: serverTimestamp() });
    };

    const deleteLog = async (id) => {
        if(window.confirm('Delete this maintenance record?')) {
            await deleteDoc(doc(db, `users/${user.uid}/maintenance`, id));
        }
    };

    const handleLogAsExpense = async (log) => {
        if (!log.cost || log.cost <= 0) {
            toast.error("Cost must be greater than $0 to log as an expense.");
            return;
        }
        if (log.expensed) {
            toast.error("This item has already been logged as an expense.");
            return;
        }
        
        try {
            await addDoc(collection(db, `users/${user.uid}/expenses`), {
                description: `Repair: ${log.listingTitle} - ${log.issue}`,
                amount: parseFloat(log.cost),
                category: 'Maintenance',
                date: new Date().toISOString().split('T')[0],
                createdAt: serverTimestamp()
            });
            
            await updateDoc(doc(db, `users/${user.uid}/maintenance`, log.id), { 
                expensed: true,
                dateUpdated: serverTimestamp()
            });
            
            toast.success("Successfully logged to expenses!");
        } catch (err) {
            console.error("Error logging expense:", err);
            toast.error("Failed to log as expense.");
        }
    };

    if (loading) return <div className="p-8 text-center text-gray-500">Loading maintenance records...</div>;

    return (
        <div className="space-y-6 animate-fade-in">
            <form onSubmit={handleAddLog} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 flex flex-col md:flex-row gap-4 items-end">
                <div className="flex-1 w-full">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1 block">Item</label>
                    <select value={newLog.listingId} onChange={e => setNewLog({...newLog, listingId: e.target.value})} className="w-full p-2.5 border rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-pink-500 outline-none transition text-sm">
                        <option value="">Select Inventory Item...</option>
                        {listings.map(l => <option key={l.id} value={l.id}>{l.title}</option>)}
                    </select>
                </div>
                <div className="flex-2 w-full md:w-1/3">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1 block">Issue Description</label>
                    <input type="text" value={newLog.issue} onChange={e => setNewLog({...newLog, issue: e.target.value})} placeholder="e.g. Torn canvas, scratched table" className="w-full p-2.5 border rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-pink-500 outline-none transition text-sm" />
                </div>
                <div className="w-full md:w-24">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1 block">Est. Cost</label>
                    <input type="number" value={newLog.cost} onChange={e => setNewLog({...newLog, cost: e.target.value})} placeholder="$0.00" className="w-full p-2.5 border rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-pink-500 outline-none transition text-sm" />
                </div>
                <button type="submit" disabled={!newLog.listingId || !newLog.issue} className="w-full md:w-auto bg-gray-900 text-white px-6 py-2.5 rounded-lg font-bold hover:bg-black transition disabled:opacity-50 h-[42px]">
                    Log Issue
                </button>
            </form>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                    <h3 className="font-bold text-gray-900">Maintenance & Repairs</h3>
                    <span className="text-xs font-semibold text-gray-500 bg-white px-2 py-1 rounded-md border border-gray-200">{logs.length} Records</span>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-white border-b border-gray-200 text-xs uppercase font-bold text-gray-400">
                            <tr>
                                <th className="px-6 py-4">Item</th>
                                <th className="px-6 py-4">Issue</th>
                                <th className="px-6 py-4">Cost</th>
                                <th className="px-6 py-4">Status</th>
                                <th className="px-6 py-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {logs.map(log => (
                                <tr key={log.id} className="hover:bg-gray-50 transition group">
                                    <td className="px-6 py-4 font-semibold text-gray-900">{log.listingTitle}</td>
                                    <td className="px-6 py-4 text-gray-600">{log.issue}</td>
                                    <td className="px-6 py-4 text-red-600 font-medium">${(log.cost || 0).toFixed(2)}</td>
                                    <td className="px-6 py-4">
                                        <select 
                                            value={log.status} 
                                            onChange={e => updateStatus(log.id, e.target.value)}
                                            className={`text-xs font-bold px-2.5 py-1 rounded-full outline-none cursor-pointer border ${log.status === 'Fixed' ? 'bg-green-50 text-green-700 border-green-200' : log.status === 'In Repair' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-red-50 text-red-700 border-red-200'}`}
                                        >
                                            <option value="Broken">Broken</option>
                                            <option value="In Repair">In Repair</option>
                                            <option value="Fixed">Fixed</option>
                                        </select>
                                    </td>
                                    <td className="px-6 py-4 text-right flex justify-end gap-2 items-center h-[72px]">
                                        {!log.expensed && (
                                            <button onClick={() => handleLogAsExpense(log)} className="text-gray-400 hover:text-green-600 transition opacity-0 group-hover:opacity-100 p-1 bg-white rounded-full shadow-sm border border-gray-100 flex items-center justify-center w-8 h-8" title="Log as Expense">
                                                💸
                                            </button>
                                        )}
                                        {log.expensed && (
                                            <span className="text-[10px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded border border-green-100">Expensed</span>
                                        )}
                                        <button onClick={() => deleteLog(log.id)} className="text-gray-400 hover:text-red-500 transition opacity-0 group-hover:opacity-100 p-1 flex items-center justify-center w-8 h-8"><PlannerIcons.Trash /></button>
                                    </td>
                                </tr>
                            ))}
                            {logs.length === 0 && (
                                <tr><td colSpan="5" className="p-8 text-center text-gray-400 italic">No items currently logged for maintenance.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function InventoryManagementTab({ listings, db }) {
    const [expandedListingId, setExpandedListingId] = useState(null);
    const [inventoryValues, setInventoryValues] = useState({});
    const toast = useToast();

    if (listings.length === 0) {
        return (
            <div className="bg-white p-6 rounded-lg shadow-lg text-center text-gray-500">
                <p>You haven't added any listings yet.</p>
                <p className="text-sm mt-1">Once you add a listing, you can manage its inventory here.</p>
            </div>
        );
    }

    const handleInventoryChange = (listingId, optionIndex, value) => {
        setInventoryValues(prev => ({
            ...prev,
            [`${listingId}-${optionIndex}`]: parseInt(value) || 0
        }));
    };

    const handleUpdateInventory = async (listing, optionIndex) => {
        const key = `${listing.id}-${optionIndex}`;
        const newInventory = inventoryValues[key];

        if (typeof newInventory !== 'number') {
            toast.error("Please enter a valid inventory number.");
            return;
        }

        const newOptions = [...listing.options];
        newOptions[optionIndex].inventory = newInventory;
        const totalInventory = newOptions.reduce((acc, opt) => acc + opt.inventory, 0);

        const listingRef = doc(db, "listings", listing.id);
        await updateDoc(listingRef, {
            options: newOptions,
            inventory: totalInventory
        });
        toast.success("Inventory updated!");
    };
    
    return (
        <div className="bg-white p-4 rounded-lg shadow-lg space-y-3">
            {listings.map(listing => (
                <div key={listing.id}>
                    <div onClick={() => setExpandedListingId(listing.id === expandedListingId ? null : listing.id)} className="flex justify-between items-center p-3 bg-gray-50 rounded-md cursor-pointer">
                        <div className="flex items-center">
                            <img src={listing.imageUrls?.[0] || 'https://placehold.co/64x64/e2e8f0/64748b?text=Img'} alt={listing.title} className="w-12 h-12 rounded-md object-cover mr-4" />
                            <div>
                                <p className="font-semibold">{listing.title}</p>
                                <p className="text-sm text-gray-500">Total Stock: {listing.inventory}</p>
                            </div>
                        </div>
                        <span className="text-sm font-semibold">{expandedListingId === listing.id ? 'Collapse' : 'Expand'}</span>
                    </div>
                    {expandedListingId === listing.id && (
                        <div className="pl-16 pr-4 py-2 space-y-2">
                            {(listing.options || []).map((option, index) => (
                                <div key={index} className="flex items-center gap-4">
                                    <p className="flex-grow font-medium">{option.name}</p>
                                    <input 
                                        type="number" 
                                        defaultValue={option.inventory} 
                                        onChange={(e) => handleInventoryChange(listing.id, index, e.target.value)}
                                        className="p-2 border rounded-md w-24"
                                    />
                                    <button onClick={() => handleUpdateInventory(listing, index)} className="bg-green-500 text-white px-3 py-1 rounded-lg text-sm font-semibold">Update</button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

function LogisticsPlanner({ user, db, functions, initialDate, onClose }) {
    const [bookings, setBookings] = useState([]);
    const [route, setRoute] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!user || !initialDate) return;

        const q = query(
            collection(db, "bookings"),
            where("vendorId", "==", user.uid),
            where("date", "==", initialDate)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const dailyBookings = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setBookings(dailyBookings.filter(b => b.deliveryMethod === 'delivery' && b.deliveryAddress)); 
            setLoading(false);
        });

        return () => unsubscribe();
    }, [user, db, initialDate]);

    const handlePlanRoute = async () => {
        if (bookings.length === 0) {
            setError("No delivery locations for this date.");
            return;
        }
        setLoading(true);
        setError('');
        setRoute(null);

        try {
            const originAddress = user.businessInfo?.address;
            if (!originAddress) {
                throw new Error("Your business address is not set. Please add it in your profile settings.");
            }

            const deliveryLocations = bookings.map(b => b.deliveryAddress);

            const allAddresses = [originAddress, ...deliveryLocations];
            const geocodePromises = allAddresses.map(address => geocodeAddress(address));
            const geocodedResults = await Promise.all(geocodePromises);

            const invalidAddressIndex = geocodedResults.findIndex(result => result === null);
            if (invalidAddressIndex !== -1) {
                const invalidAddress = allAddresses[invalidAddressIndex];
                const errorMessage = invalidAddressIndex === 0 
                    ? `Your business address ("${invalidAddress}") could not be found. Please update it in your profile settings.`
                    : `The delivery address "${invalidAddress}" for one of your bookings could not be found. Please check the booking details.`;
                throw new Error(errorMessage);
            }

            const planDeliveryRoute = httpsCallable(functions, 'planDeliveryRoute');
            const result = await planDeliveryRoute({ originAddress, deliveryLocations });

            const sortedBookings = result.data.optimizedRoute.map(address =>
                bookings.find(b => b.deliveryAddress === address)
            ).filter(Boolean);

            setRoute({
                ...result.data,
                optimizedBookings: sortedBookings
            });

        } catch (err) {
            console.error("Error planning route:", err);
            setError(err.message || "An unknown error occurred while planning the route.");
        } finally {
            setLoading(false);
        }
    };

    const generateGoogleMapsUrl = (origin, waypoints) => {
        const baseUrl = "https://www.google.com/maps/dir/?api=1";
        const originParam = `&origin=${encodeURIComponent(origin)}`;
        const destinationParam = `&destination=${encodeURIComponent(origin)}`; 
        const waypointsParam = `&waypoints=${waypoints.map(addr => encodeURIComponent(addr)).join('|')}`;
        return `${baseUrl}${originParam}${destinationParam}${waypointsParam}`;
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
            <div className="bg-white p-8 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <h2 className="text-2xl font-bold mb-4">Delivery Route Planner for {initialDate}</h2>
                {loading && !route && <p>Loading deliveries...</p>}
                {error && <p className="text-red-500">{error}</p>}

                {!loading && bookings.length === 0 && <p>No deliveries scheduled for this date.</p>}

                {!route && bookings.length > 0 && (
                    <div>
                        <p className="font-semibold mb-2">Found {bookings.length} deliver{bookings.length > 1 ? 'ies' : 'y'}:</p>
                        <ul className="list-disc list-inside mb-4">
                            {bookings.map(b => (
                                <li key={b.id}>
                                    {b.deliveryAddress} ({b.listingTitle}) 
                                    <span className="text-gray-500 text-sm ml-2">- {b.deliveryTime ? new Date(`1970-01-01T${b.deliveryTime}`).toLocaleTimeString([], {hour: 'numeric', minute:'2-digit'}) : 'Flexible'}</span>
                                </li>
                            ))}
                        </ul>
                        <button onClick={handlePlanRoute} disabled={loading} className="w-full bg-purple-600 text-white py-2 rounded-lg font-semibold hover:bg-purple-700">
                            {loading ? 'Planning...' : 'Optimize Route'}
                        </button>
                    </div>
                )}

                {route && (
                    <div>
                        <h3 className="text-xl font-semibold mb-2">Optimized Delivery Route</h3>
                        <ol className="list-decimal list-inside space-y-2">
                            {route.optimizedBookings.map((booking, index) => (
                                <li key={booking.id}>
                                    <strong>Stop {index + 1}:</strong> {booking.deliveryAddress}
                                    <p className="text-sm text-gray-600 pl-4">
                                        - {booking.quantity}x {booking.listingTitle} for {booking.renterName} 
                                        <span className="font-semibold text-pink-600 ml-2">
                                            (Target: {booking.deliveryTime ? new Date(`1970-01-01T${booking.deliveryTime}`).toLocaleTimeString([], {hour: 'numeric', minute:'2-digit'}) : 'Flexible'})
                                        </span>
                                    </p>
                                </li>
                            ))}
                        </ol>
                        <div className="mt-4 pt-4 border-t text-sm">
                            <p><strong>Total Distance:</strong> {(route.totalDistanceMeters * 0.000621371).toFixed(2)} miles</p>
                            <p><strong>Estimated Duration:</strong> {Math.round(route.totalDurationSeconds / 60)} minutes</p>
                            <a href={generateGoogleMapsUrl(user.businessInfo.address, route.optimizedRoute)} target="_blank" rel="noopener noreferrer" className="mt-4 inline-block bg-blue-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-600">
                                Open in Google Maps
                            </a>
                        </div>
                    </div>
                )}

                <div className="mt-6 flex justify-end">
                    <button onClick={onClose} className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg font-semibold hover:bg-gray-300">Close</button>
                </div>
            </div>
        </div>
    );
}

function VendorPayouts({ user, functions, isUserAuthenticated, bookings }) {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const transactionHistory = useMemo(() => {
        if (!bookings) return [];
        const payments = [];
        bookings.forEach(booking => {
            if (booking.payments && Array.isArray(booking.payments)) {
                booking.payments.forEach(payment => {
                    payments.push({
                        id: payment.stripePaymentId || `${booking.id}_${payment.date?.seconds || Date.now()}`,
                        amount: payment.amount,
                        date: payment.date?.toDate ? payment.date.toDate() : (payment.date?.seconds ? new Date(payment.date.seconds * 1000) : new Date()),
                        type: payment.type,
                        bookingTitle: booking.listingTitle,
                        bookingId: booking.id,
                        renterName: booking.renterName
                    });
                });
            }
        });
        return payments.sort((a, b) => b.date - a.date);
    }, [bookings]);

    const handleOnboarding = async () => {
        setIsLoading(true);
        setError('');

        if (!functions) {
            setError("Configuration Error: Functions not initialized.");
            setIsLoading(false);
            return;
        }

        try {
            const createStripeAccount = httpsCallable(functions, 'createStripeAccount');
            let origin = window.location.origin;
            if (!origin || origin === 'null' || origin.includes('about:') || origin.includes('file:')) {
                origin = 'https://hostitdotcom.firebaseapp.com';
            }

            const result = await createStripeAccount({ 
                origin: origin,
                returnUrl: `${origin}/#dashboard`,
                refreshUrl: `${origin}/#dashboard`,
                email: user.email,
                uid: user.uid
            });

            if (result.data && result.data.url) {
                window.location.href = result.data.url;
            } else {
                throw new Error("Server returned an invalid response.");
            }
        } catch (err) {
            console.error("Stripe onboarding failed:", err);
            const msg = err.message && err.message.includes('internal')
                ? "Connection failed (Internal Error). Please check backend configuration."
                : (err.message || "Failed to start Stripe onboarding.");
            setError(msg);
            setIsLoading(false);
        }
    };

    if (!user || !isUserAuthenticated) {
        return (
            <div className="bg-white p-6 rounded-lg shadow-lg">
                <p>Please log in to manage payouts.</p>
            </div>
        );
    }
    
    if (user.isVendor && !user.stripeAccountId) {
        return (
             <div className="bg-white p-6 rounded-lg shadow-lg">
                <h3 className="text-xl font-bold mb-4">Set Up Payouts</h3>
                <p className="text-gray-600 mb-4">
                    To receive payments for your rentals, you need to set up a Standard account with Stripe.
                </p>
                <button onClick={handleOnboarding} disabled={isLoading} className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400">
                    {isLoading ? 'Redirecting...' : 'Connect with Stripe'}
                </button>
                 {error && <p className="text-red-500 mt-2">{error}</p>}
            </div>
        )
    }

    return (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-lg">
                <h3 className="text-xl font-bold mb-4">Payouts Dashboard</h3>
                <p className="text-gray-600 mb-4">
                Your account is connected to Stripe. You can view your balance, manage payouts, and configure your bank details directly on your Stripe Standard dashboard.
                </p>
                <a href="https://dashboard.stripe.com/" target="_blank" rel="noopener noreferrer" className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition">
                    Go to Stripe Dashboard
                </a>
                {error && <p className="text-red-500 mt-2">{error}</p>}
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-100">
                <h3 className="text-xl font-bold mb-4 text-gray-800">Recent Transactions</h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 text-gray-500 border-b">
                            <tr>
                                <th className="p-3 font-semibold">Date</th>
                                <th className="p-3 font-semibold">Booking Details</th>
                                <th className="p-3 font-semibold">Type</th>
                                <th className="p-3 font-semibold text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {transactionHistory.length > 0 ? (
                                transactionHistory.map((tx) => (
                                    <tr key={tx.id} className="hover:bg-gray-50 transition">
                                        <td className="p-3 text-gray-600 whitespace-nowrap">
                                            {tx.date.toLocaleDateString()}
                                            <span className="block text-xs text-gray-400">{tx.date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                        </td>
                                        <td className="p-3">
                                            <p className="font-medium text-gray-900 truncate max-w-[200px]">{tx.bookingTitle}</p>
                                            <p className="text-xs text-gray-500">{tx.renterName}</p>
                                        </td>
                                        <td className="p-3">
                                            <span className={`px-2 py-1 rounded-full text-xs font-bold capitalize ${tx.type === 'final' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                                {tx.type === 'initial' ? 'Deposit' : tx.type}
                                            </span>
                                        </td>
                                        <td className="p-3 text-right font-bold text-green-600">
                                            +${tx.amount.toFixed(2)}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="p-8 text-center text-gray-500 italic">
                                        No transactions recorded yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function CrmClientModal({ user, db, client, onClose }) {
    const [formData, setFormData] = useState({
        name: client?.name || '',
        email: client?.email || '',
        phone: client?.phone || '',
        status: client?.status || 'Lead',
        eventDate: client?.eventDate || '',
        value: client?.value || '',
    });
    const [notes, setNotes] = useState(client?.notes || []);
    const [newNote, setNewNote] = useState('');
    const [saving, setSaving] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddNote = () => {
        if (!newNote.trim()) return;
        setNotes([{ text: newNote, date: new Date().toISOString() }, ...notes]);
        setNewNote('');
    };

    const handleRemoveNote = (idx) => {
        setNotes(notes.filter((_, i) => i !== idx));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const dataToSave = {
                ...formData,
                value: parseFloat(formData.value) || 0,
                notes,
                updatedAt: serverTimestamp()
            };

            if (client?.id) {
                await updateDoc(doc(db, `users/${user.uid}/crm_clients`, client.id), dataToSave);
            } else {
                dataToSave.createdAt = serverTimestamp();
                await addDoc(collection(db, `users/${user.uid}/crm_clients`), dataToSave);
            }
            onClose();
        } catch (err) {
            console.error("Error saving client:", err);
            alert("Failed to save client.");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!client?.id || !window.confirm("Are you sure you want to delete this client record?")) return;
        setSaving(true);
        try {
            await deleteDoc(doc(db, `users/${user.uid}/crm_clients`, client.id));
            onClose();
        } catch (err) {
            console.error("Error deleting client:", err);
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center p-6 border-b border-gray-100">
                    <h2 className="text-2xl font-bold">{client ? 'Edit Client Details' : 'Add New Lead'}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-800 text-2xl leading-none">&times;</button>
                </div>
                
                <div className="flex-1 overflow-y-auto p-6">
                    <form id="crm-form" onSubmit={handleSave} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Full Name*</label>
                                <input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-pink-500 outline-none" required />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
                                <select name="status" value={formData.status} onChange={handleChange} className="w-full p-2.5 border rounded-lg bg-white focus:ring-2 focus:ring-pink-500 outline-none">
                                    <option value="Lead">New Lead</option>
                                    <option value="Contacted">Contacted</option>
                                    <option value="Negotiation">In Negotiation</option>
                                    <option value="Booked">Booked</option>
                                    <option value="Lost">Lost</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
                                <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-pink-500 outline-none" />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Phone</label>
                                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-pink-500 outline-none" />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Potential Value ($)</label>
                                <input type="number" name="value" value={formData.value} onChange={handleChange} className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-pink-500 outline-none" placeholder="0.00" />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Event Date</label>
                                <input type="date" name="eventDate" value={formData.eventDate} onChange={handleChange} className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-pink-500 outline-none bg-white" />
                            </div>
                        </div>

                        <div className="border-t pt-6">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Internal Notes</label>
                            <div className="flex gap-2 mb-4">
                                <input 
                                    type="text" 
                                    value={newNote} 
                                    onChange={e => setNewNote(e.target.value)} 
                                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddNote())}
                                    placeholder="Add a quick note..." 
                                    className="flex-grow p-2.5 border rounded-lg focus:ring-2 focus:ring-pink-500 outline-none" 
                                />
                                <button type="button" onClick={handleAddNote} className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 rounded-lg font-semibold transition">Add</button>
                            </div>
                            <div className="space-y-3 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                                {notes.length > 0 ? notes.map((note, idx) => (
                                    <div key={idx} className="bg-yellow-50 p-3 rounded-lg border border-yellow-100 flex justify-between items-start group">
                                        <div>
                                            <p className="text-sm text-gray-800">{note.text}</p>
                                            <p className="text-[10px] text-gray-500 mt-1 uppercase font-bold">{new Date(note.date).toLocaleString()}</p>
                                        </div>
                                        <button type="button" onClick={() => handleRemoveNote(idx)} className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity">×</button>
                                    </div>
                                )) : (
                                    <p className="text-sm text-gray-400 italic">No notes added yet.</p>
                                )}
                            </div>
                        </div>
                    </form>
                </div>
                
                <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-between items-center rounded-b-xl">
                    {client?.id ? (
                        <button type="button" onClick={handleDelete} className="text-red-600 hover:bg-red-50 px-4 py-2 rounded-lg font-semibold transition text-sm">Delete Client</button>
                    ) : <div></div>}
                    <div className="flex gap-3">
                        <button type="button" onClick={onClose} className="px-5 py-2.5 bg-white border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition">Cancel</button>
                        <button type="submit" form="crm-form" disabled={saving} className="px-6 py-2.5 bg-pink-600 text-white rounded-lg font-bold hover:bg-pink-700 transition shadow-md disabled:bg-pink-300">
                            {saving ? 'Saving...' : 'Save Client'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function AcceptBookingModal({ booking, onClose, onConfirm }) {
    const [paymentType, setPaymentType] = useState('full');
    const [depositPercentage, setDepositPercentage] = useState(50);
    const [deadlineDays, setDeadlineDays] = useState(2); // Default to 2 days (48 hours)
    const [error, setError] = useState('');

    const [installments, setInstallments] = useState([
        { id: 1, amount: ((booking?.totalCost || 0) / 2).toFixed(2), deadlineDays: 2 },
        { id: 2, amount: ((booking?.totalCost || 0) / 2).toFixed(2), deadlineDays: 30 }
    ]);

    const handleAddInstallment = () => setInstallments([...installments, { id: Date.now(), amount: '', deadlineDays: 30 }]);
    const handleRemoveInstallment = (id) => setInstallments(installments.filter(i => i.id !== id));
    const handleInstallmentChange = (id, field, value) => setInstallments(installments.map(i => i.id === id ? { ...i, [field]: value } : i));

    const depositAmount = useMemo(() => {
        if (!booking || paymentType !== 'deposit') return 0;
        return ((booking.totalCost || 0) * (depositPercentage / 100)).toFixed(2);
    }, [booking, paymentType, depositPercentage]);

    const handleConfirm = () => {
        if (paymentType !== 'no_payment' && paymentType !== 'installments' && deadlineDays <= 0) {
            setError('Payment deadline must be at least 1 day.');
            return;
        }

        const deadline = new Date();
        deadline.setDate(deadline.getDate() + parseInt(deadlineDays, 10));

        let paymentTerms = {};

        if (paymentType === 'installments') {
            const totalInst = installments.reduce((acc, i) => acc + parseFloat(i.amount || 0), 0);
            if (Math.abs(totalInst - (booking?.totalCost || 0)) > 0.02) {
                setError("Installment amounts ($" + totalInst.toFixed(2) + ") must equal the total cost ($" + (booking?.totalCost || 0).toFixed(2) + ").");
                return;
            }
            paymentTerms = {
                type: 'installments',
                deadline: new Date(Date.now() + parseInt(installments[0].deadlineDays, 10) * 24 * 60 * 60 * 1000),
                schedule: installments.map((inst, index) => {
                    const d = new Date();
                    d.setDate(d.getDate() + parseInt(inst.deadlineDays, 10));
                    return { 
                        id: inst.id, 
                        index: index + 1,
                        amount: parseFloat(inst.amount), 
                        deadline: d, 
                        status: 'pending' 
                    };
                })
            };
        } else {
            paymentTerms = {
                type: paymentType,
                amount: paymentType === 'full' ? booking.totalCost : (paymentType === 'deposit' ? parseFloat(depositAmount) : 0),
                deadline: paymentType === 'no_payment' ? null : deadline,
            };
        }
        onConfirm(booking, paymentTerms);
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200] p-4 animate-fade-in">
            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-2xl max-w-md w-full">
                <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-3">
                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"><span>✅</span> Accept Booking</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-red-500 transition text-2xl leading-none">&times;</button>
                </div>
                <p className="mb-6 text-gray-600 text-sm leading-relaxed">Secure this booking by requesting a payment from the renter. The item's availability will be reserved once you confirm.</p>
                <div className="space-y-5">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Payment Request Type</label>
                        <select value={paymentType} onChange={(e) => setPaymentType(e.target.value)} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none bg-white font-medium">
                            <option value="full">Request Full Payment (${(booking?.totalCost || 0).toFixed(2)})</option>
                            <option value="deposit">Request a Deposit</option>
                            <option value="installments">Payment Schedule / Installments</option>
                            <option value="no_payment">Accept (No Payment Required)</option>
                        </select>
                    </div>

                    {paymentType === 'installments' && (
                        <div className="animate-fade-in space-y-3 bg-gray-50 p-4 rounded-xl border border-gray-100">
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">Payment Schedule</label>
                            {installments.map((inst, idx) => (
                                <div key={inst.id} className="flex gap-2 items-center bg-white p-2 rounded-lg border border-gray-200 shadow-sm">
                                    <span className="text-xs font-bold text-gray-400 w-4">{idx + 1}.</span>
                                    <div className="flex-1">
                                        <div className="relative">
                                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-bold">$</span>
                                            <input type="number" value={inst.amount} onChange={(e) => handleInstallmentChange(inst.id, 'amount', e.target.value)} className="w-full pl-6 p-2 border border-gray-200 rounded text-sm outline-none focus:border-green-500" placeholder="Amount" />
                                        </div>
                                    </div>
                                    <div className="flex-1 flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded px-2">
                                        <input type="number" value={inst.deadlineDays} onChange={(e) => handleInstallmentChange(inst.id, 'deadlineDays', e.target.value)} className="w-10 p-2 bg-transparent text-sm font-bold text-center outline-none" />
                                        <span className="text-[10px] font-bold text-gray-500 uppercase">days</span>
                                    </div>
                                    <button onClick={() => handleRemoveInstallment(inst.id)} className="text-red-400 hover:text-red-600 p-1.5 transition"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg></button>
                                </div>
                            ))}
                            <div className="flex justify-between items-center pt-2">
                                <button onClick={handleAddInstallment} className="text-xs bg-blue-50 text-blue-600 px-3 py-1.5 rounded-lg font-bold hover:bg-blue-100 transition shadow-sm">+ Add Payment</button>
                                <span className={`text-xs font-bold px-3 py-1.5 rounded-lg ${installments.reduce((a, b) => a + parseFloat(b.amount || 0), 0) === (booking?.totalCost || 0) ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                                    Total: ${installments.reduce((a, b) => a + parseFloat(b.amount || 0), 0).toFixed(2)} / ${(booking?.totalCost || 0).toFixed(2)}
                                </span>
                            </div>
                        </div>
                    )}

                    {paymentType !== 'no_payment' && paymentType !== 'installments' && (
                        <div className="animate-fade-in space-y-5 bg-gray-50 p-4 rounded-xl border border-gray-100">
                                {paymentType === 'deposit' && (
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide">Deposit Amount</label>
                                        <span className="font-black text-lg text-green-600">${depositAmount}</span>
                                    </div>
                                    <div className="flex items-center gap-4 bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                        <input type="range" min="10" max="100" step="5" value={depositPercentage} onChange={(e) => setDepositPercentage(e.target.value)} className="w-full accent-green-500" />
                                        <span className="font-bold text-gray-700 w-12 text-right">{depositPercentage}%</span>
                                    </div>
                                </div>
                            )}
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">Payment Deadline</label>
                                <div className="flex items-center gap-3 bg-white p-2 rounded-lg border border-gray-200 shadow-sm w-fit">
                                    <input type="number" value={deadlineDays} onChange={(e) => setDeadlineDays(e.target.value)} min="1" className="p-1.5 w-16 text-center text-sm font-bold outline-none focus:text-green-600" />
                                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wide pr-3">days from now</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                {error && <p className="text-red-500 text-sm mt-4 font-semibold p-3 bg-red-50 rounded-lg border border-red-100">{error}</p>}
                <div className="mt-8 flex justify-end gap-3 pt-4 border-t border-gray-100">
                    <button onClick={onClose} className="px-5 py-2.5 text-gray-600 font-semibold hover:bg-gray-100 rounded-lg transition text-sm">Cancel</button>
                    <button onClick={handleConfirm} className="px-6 py-2.5 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition shadow-md text-sm">Confirm & Send Request</button>
                </div>
            </div>
        </div>
    );
}

function DeclineConfirmationModal({ booking, onClose, onConfirm }) {
    const [isProcessing, setIsProcessing] = useState(false);

    const handleConfirm = async () => {
        setIsProcessing(true);
        await onConfirm(booking);
        setIsProcessing(false);
    };

    if (!booking) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[300] flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-2xl max-w-md w-full p-6 md:p-8 shadow-2xl">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 text-center mb-2">Decline Booking?</h3>
                <p className="text-gray-600 text-sm text-center mb-6">Are you sure you want to decline the booking for <strong>{booking.listingTitle}</strong>? This action cannot be undone and the customer will be notified via email.</p>
                
                <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t border-gray-100">
                    <button onClick={onClose} disabled={isProcessing} className="w-full sm:w-auto px-5 py-2.5 text-gray-600 font-semibold hover:bg-gray-100 rounded-lg transition">Cancel</button>
                    <button onClick={handleConfirm} disabled={isProcessing} className="w-full sm:w-auto px-5 py-2.5 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 transition shadow-md disabled:bg-red-300 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                        {isProcessing ? <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span> : null}
                        {isProcessing ? 'Declining...' : 'Yes, Decline'}
                    </button>
                </div>
            </div>
        </div>
    );
}